import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { formatToLocalDate } from '../../../utils/dateUtils'
// TalentOS match reporting — everything is under components/talentos.
import {
  TalentosMatchReportBody,
  TalentosMatchReportFooter,
  TalentosScoreCell,
  toDisplayPercent,
  useTalentosMatchesForJob,
} from '../../../components/talentos'
import {
  ArrowLeftIcon,
  BriefcaseIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
  CpuChipIcon,
  AcademicCapIcon,
  LightBulbIcon,
  StarIcon,
  EyeIcon,
  BeakerIcon,
  QuestionMarkCircleIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  XMarkIcon,
  CheckIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  CogIcon,
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  TrashIcon,
  PencilIcon,
  InformationCircleIcon,
  MicrophoneIcon,
  ShieldCheckIcon,
  VideoCameraIcon,
  TagIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline'

import { jobsApi, matchingApi, resumesApi, type JobAssignment } from '../../../services/api'
import { formatResumeWithTemplate } from '@/services/resumeTemplateApi'
import { interviewApi, type InterviewGuide } from '../../../services/interviewApi'
import { type ExtendedInterviewGuide } from '../../../types/interview'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import MarkdownRenderer from '../../../components/common/MarkdownRenderer'
import CollapsibleCard from '../../../components/common/CollapsibleCard'
import TalentosJobProfileTab from '../../../components/jobs/TalentosJobProfileTab'
import InterviewModal from '../../../components/InterviewModal'
import InterviewTab from '../../../components/interview/InterviewTab'
import ProctoredInterviewTab from '../../../components/proctoredInterview/ProctoredInterviewTab'
import LiveInterviewTab from '../../../components/liveInterview/LiveInterviewTab'
// import VoiceScreeningTab from '../../../components/voiceScreening/VoiceScreeningTab'
import AIInterviewButton from '../../../components/interview/AIInterviewButton'
import ApplicationStatusDropdown from '../../../components/matching/ApplicationStatusDropdown'
import MatchingStatusBadge from '../../../components/matching/MatchingStatusBadge'
import StageAcknowledgementPanel from '../../../components/matching/StageAcknowledgementPanel'
import { FilterPanel, ResultCountDisplay } from '../../../components/matching'
import { useMatchFilters } from '../../../hooks/useMatchFilters'
import { useCandidateStages } from '../../../hooks/useCandidateStages'
import { useAuth } from '../../../hooks/useAuth'
import { usePermission } from '@/hooks/usePermission'
import { downloadJobAsWord } from '../../../utils/downloadJobAsWord'
import { getHighestDegreeAndField } from '../../../utils/educationSummary'
import Tooltip from '../../../components/common/Tooltip'
import ConfirmationModal from '../../../components/common/ConfirmationModal'
import { AssignedRecruitersDisplay } from '../../../components/jobs/AssignedRecruitersDisplay'
import { AssignmentManagerModal } from '../../../components/jobs/AssignmentManagerModal'
import SourceSelector from '../../../components/common/SourceSelector'
import AssigneeSelector from '../../../components/jobs/AssigneeSelector'
import type { OrganizationUser } from '../../../services/assignmentApi'
import DuplicateDetectionModal, { type DuplicateDetectionData } from '../../../components/resume/DuplicateDetectionModal'
import { useBulkApply } from '../../../contexts/BulkApplyContext'
import {
  AssignmentBadge,
  EditAssignmentButton,
  ViewAuditTrailButton,
  AssignmentEditPopover,
  AuditTrailModal,
} from '../../../components/assignment'

// Job Overview redesign (2026-10-05): a TalentOS-analysed job
// (job.analysis_engine === 'talentos') shows a single TalentOS-sourced
// "Job Overview" tab (TalentosJobProfileTab) instead of the Foundry-sourced
// Overview/Required Skills/Experience tabs -- same engine-flag pattern as
// ResumeDetailPage.tsx's SHOW_TALENTOS_RESUME_UI. Apply Matching and every
// Interview tab stay identical either way, per explicit instruction. Flip
// back to false only if this needs to be rolled back.
const SHOW_TALENTOS_JOB_UI = true

interface Job {
  id: string
  job_id?: string
  title: string
  department?: string
  client?: string
  source_id?: string            
  source_name?: string          
  source_is_active?: boolean 
  location?: string
  employment_type: string
  remote_option: string
  description: string
  responsibilities?: string
  requirements?: string
  benefits?: string
  salary_min?: number
  salary_max?: number
  salary_currency: string
  salary_period?: string
  application_deadline?: string
  min_years_experience?: number
  max_years_experience?: number
  status: string
  processing_status?: string
  // Which engine produced this job's CURRENT analysis ("foundry" or
  // "talentos") -- see database.models.Job.analysis_engine.
  analysis_engine?: string
  created_at: string
  updated_at: string
  ai_analysis?: any
  ai_processed_requirements?: any
  skills_analysis?: string
  experience_assessment?: string
  interview_questions?: string
  analysis_generated_at?: string
  education_requirements?: any
  processing_confidence?: number
  assigned_recruiters?: JobAssignment[]
  // Set only for a job created via "Upload JD" -- see database.models.Job's
  // docstring on these same two columns (also azure_blob_name/azure_container,
  // not needed by the frontend directly since GET /jobs/{id}/download already
  // knows them server-side).
  original_filename?: string
  content_type?: string
}

interface Resume {
  id: string
  candidate_id?: string
  candidate_name: string
  candidate_email?: string
  candidate_phone?: string
  professional_title?: string
  location?: string
  total_years_experience?: number
  skills?: string[]
  education?: any[]
  work_experience?: any[]
  blacklist?: boolean
  created_at?: string
  updated_at?: string
}

interface MatchResult {
  resume_id: string
  uploaded_by_name?: string
  candidate_name: string
  candidate_email?: string
  overall_score: number
  skill_score: number
  experience_score: number
  education_score: number
  location_score: number
  detailed_analysis: any
  strengths: string[]
  concerns: string[]
  recommendation: string
  confidence: number
  blacklist?: boolean
  matched_skills?: Array<{ skill_name: string; match_score: number }>
  missing_skills?: Array<{ skill_name: string; match_score: number }>
}

interface JobFormData {
  title: string
  department?: string
  client?: string
  source_id?: string
  location?: string
  employment_type: string
  remote_option: string
  description: string
  responsibilities?: string
  requirements?: string
  benefits?: string
  salary_min?: number
  salary_max?: number
  salary_currency: string
  salary_period: string
  application_deadline?: string
  min_years_experience?: number
  max_years_experience?: number
  assigned_user_ids?: string[]
}

// Phone number formatting utility
const formatPhoneNumber = (phone: string): string => {
  if (!phone) return ''
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')
  
  // Format based on length
  if (digits.length === 10) {
    // US format: 123-456-7890
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  } else if (digits.length === 11 && digits.startsWith('1')) {
    // US format with country code: 1-123-456-7890
    return `1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`
  } else if (digits.length === 11) {
    // International format: 123-456-78901
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  } else if (digits.length === 12) {
    // International format with country code: 123-456-789012
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  } else {
    // For other lengths, just add dashes every 3 digits
    return digits.replace(/(\d{3})(?=\d)/g, '$1-')
  }
}

// Resume Popup Modal Component
const ResumePopupModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  resumeData: any
  isLoading: boolean
  error?: any
}> = ({ isOpen, onClose, resumeData, isLoading, error: resumeError }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'skills' | 'experience' | 'education' | 'agents'>('overview')

  if (!isOpen) return null

  // Tab configuration
  const tabs = [
    { id: 'overview', name: 'Overview', icon: EyeIcon },
    { id: 'skills', name: 'Skills Analysis', icon: CpuChipIcon },
    { id: 'experience', name: 'Experience', icon: BriefcaseIcon },
    { id: 'education', name: 'Education', icon: AcademicCapIcon },
    { id: 'agents', name: 'Analysis', icon: BeakerIcon }
  ]

  const resume = resumeData?.data

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-0 border w-[95%] max-w-7xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">Resume Details</h2>
            {resume?.candidate_name && (
              <span className="text-sm text-gray-500">- {resume.candidate_name}</span>
            )}
          </div>
                     <div className="flex items-center space-x-2">
                       <button
                         onClick={onClose}
                         className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
                       >
                         <XMarkIcon className="w-6 h-6" />
                       </button>
                     </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto scrollbar-hide px-6">
            <div className="flex space-x-8 min-w-max">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                )
              })}
            </div>
          </nav>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-140px)]">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : resumeError ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <ExclamationCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <p className="text-red-500 mb-2">Failed to load resume data</p>
                <p className="text-gray-500 text-sm">{resumeError.message || 'Please try again'}</p>
              </div>
            </div>
          ) : resume ? (
            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Professional Summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center space-x-2">
                      <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                      <span>Professional Summary</span>
                    </h3>
                    <div className="bg-white border border-blue-100 rounded-lg p-4">
                      <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                        {resume.analysis?.final_summary || resume.analysis?.personal_info?.professional_title || 'Summary not available.'}
                      </p>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                        <UserIcon className="w-5 h-5 text-blue-600" />
                        <span>Personal Information</span>
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <UserIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{resume.analysis?.personal_info?.full_name || resume.candidate_name}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <EnvelopeIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{resume.analysis?.personal_info?.email || resume.candidate_email}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <PhoneIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{resume.analysis?.personal_info?.phone || resume.candidate_phone}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <MapPinIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{resume.analysis?.personal_info?.location || 'Location not specified'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                        <BriefcaseIcon className="w-5 h-5 text-blue-600" />
                        <span>Professional Details</span>
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <BriefcaseIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{resume.analysis?.personal_info?.professional_title || resume.professional_title || 'Title not specified'}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <ClockIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-900">{resume.total_years_experience ? `${resume.total_years_experience} years experience` : 'Experience not specified'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Skills Tab */}
              {activeTab === 'skills' && (
                <div className="space-y-6">
                  {/* Core Competencies */}
                  {resume.analysis?.skills_analysis?.detailed_table && (
                    (() => {
                      const dt = resume.analysis!.skills_analysis!.detailed_table as Record<string, any[]>;
                      const allRows: any[] = Object.values(dt).flat().filter(Array.isArray).length
                        ? (Object.values(dt).flat() as any[])
                        : ([] as any[]);
                      const isCore = (row: any) =>
                        String(row?.core_or_supporting || '').toLowerCase().startsWith('core');
                      const coreRows = allRows
                        .filter((r) => isCore(r))
                        .sort((a, b) => (typeof b.relevance_score === 'number' ? b.relevance_score : 0) - (typeof a.relevance_score === 'number' ? a.relevance_score : 0));
                      const seen = new Set<string>();
                      const topCore = coreRows.filter((r) => {
                        const key = String(r.skill || '').toLowerCase();
                        if (!key || seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      }).slice(0, 12);
                      if (!topCore.length) return null;
                      return (
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                            <StarIcon className="w-5 h-5 text-yellow-500" />
                            <span>Core Competencies</span>
                            <span className="text-sm text-gray-500 font-normal">(Ranked by AI Relevance Score)</span>
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {topCore.map((r, i) => (
                              <span key={`${r.skill}-${i}`} className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 text-blue-800 px-3 py-2 rounded-full text-sm font-medium border border-blue-200">
                                {r.skill}
                                {typeof r.relevance_score === 'number' && (
                                  <span className="ml-2 text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                                    {Math.round(r.relevance_score * 100)}%
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()
                  )}

                  {/* Technical Skills */}
                  {resume.analysis?.skills_analysis?.technical_skills && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                        <CpuChipIcon className="w-5 h-5 text-blue-600" />
                        <span>Technical Skills</span>
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {resume.analysis.skills_analysis.technical_skills.map((skill: any, index: number) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                            {typeof skill === 'string' ? skill : skill?.skill || skill?.name || String(skill)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Programming Languages */}
                  {resume.analysis?.skills_analysis?.programming_languages && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Programming Languages</h3>
                      <div className="flex flex-wrap gap-2">
                        {resume.analysis.skills_analysis.programming_languages.map((lang: any, index: number) => (
                          <span key={index} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                            {typeof lang === 'string' ? lang : lang?.skill || lang?.name || String(lang)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Frameworks & Libraries */}
                  {resume.analysis?.skills_analysis?.frameworks_libraries && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Frameworks & Libraries</h3>
                      <div className="flex flex-wrap gap-2">
                        {resume.analysis.skills_analysis.frameworks_libraries.map((framework: any, index: number) => (
                          <span key={index} className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                            {typeof framework === 'string' ? framework : framework?.skill || framework?.name || String(framework)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tools & Technologies */}
                  {resume.analysis?.skills_analysis?.tools_technologies && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Tools & Technologies</h3>
                      <div className="flex flex-wrap gap-2">
                        {resume.analysis.skills_analysis.tools_technologies.map((tool: any, index: number) => (
                          <span key={index} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                            {typeof tool === 'string' ? tool : tool?.skill || tool?.name || String(tool)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Soft Skills */}
                  {resume.analysis?.skills_analysis?.soft_skills && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Soft Skills & Leadership</h3>
                      <div className="flex flex-wrap gap-2">
                        {resume.analysis.skills_analysis.soft_skills.map((skill: any, index: number) => (
                          <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                            {typeof skill === 'string' ? skill : skill?.skill || skill?.name || String(skill)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skill Categories */}
                  {resume.analysis?.skills_analysis?.skill_categories && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Skill Categories</h3>
                      <div className="space-y-4">
                        {Object.entries(resume.analysis.skills_analysis.skill_categories).map(([category, skills]: [string, any], index: number) => (
                          <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-2 capitalize">{category}</h4>
                            <div className="flex flex-wrap gap-2">
                              {Array.isArray(skills) && skills.map((skill: any, skillIndex: number) => (
                                <span key={skillIndex} className="bg-white text-gray-700 px-2 py-1 rounded text-sm border">
                                  {typeof skill === 'string' ? skill : skill?.skill || skill?.name || String(skill)}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Certifications */}
                  {resume.analysis?.skills_analysis?.certifications && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Certifications</h3>
                      <div className="flex flex-wrap gap-2">
                        {resume.analysis.skills_analysis.certifications.map((cert: any, index: number) => (
                          <span key={index} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                            {typeof cert === 'string' ? cert : cert?.skill || cert?.name || String(cert)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Comprehensive Skills Table */}
                  {(() => {
                    // Aggregate all skills from different categories into one table
                    const skillsAnalysis = resume.analysis?.skills_analysis;
                    if (!skillsAnalysis) return null;

                    const allSkills: any[] = [];
                    
                    // Helper function to add skills from a category
                    const addSkillsFromCategory = (skills: any[], category: string) => {
                      if (Array.isArray(skills)) {
                        skills.forEach(skill => {
                          if (typeof skill === 'object' && skill.skill) {
                            allSkills.push({ ...skill, category });
                          } else if (typeof skill === 'string') {
                            allSkills.push({ 
                              skill, 
                              category,
                              proficiency: '-',
                              relevance_score: 0.5,
                              frequency_of_use: '-',
                              years_experience: '-',
                              core_or_supporting: 'Supporting',
                              current_or_outdated: 'Current',
                              context_demonstrated: '-',
                              evidence_of_expertise: '-',
                              emerging: false
                            });
                          }
                        });
                      }
                    };

                    // Add skills from all categories
                    addSkillsFromCategory(skillsAnalysis.technical_skills, 'Technical');
                    addSkillsFromCategory(skillsAnalysis.programming_languages, 'Programming');
                    addSkillsFromCategory(skillsAnalysis.frameworks_libraries, 'Frameworks');
                    addSkillsFromCategory(skillsAnalysis.tools_technologies, 'Tools');
                    addSkillsFromCategory(skillsAnalysis.soft_skills, 'Soft Skills');
                    addSkillsFromCategory(skillsAnalysis.languages, 'Languages');
                    addSkillsFromCategory(skillsAnalysis.methodologies, 'Methodologies');
                    addSkillsFromCategory(skillsAnalysis.certifications, 'Certifications');
                    addSkillsFromCategory(skillsAnalysis.emerging_skills, 'Emerging');
                    addSkillsFromCategory(skillsAnalysis.key_competencies, 'Key Competencies');

                    if (allSkills.length === 0) return null;

                    // Remove duplicates based on skill name
                    const uniqueSkills = allSkills.filter((skill, index, self) => 
                      index === self.findIndex(s => s.skill.toLowerCase() === skill.skill.toLowerCase())
                    );

                    // Sort by relevance score and core skills first
                    const sortedSkills = uniqueSkills.sort((a, b) => {
                      const aCore = String(a?.core_or_supporting || '').toLowerCase().startsWith('core') ? 1 : 0;
                      const bCore = String(b?.core_or_supporting || '').toLowerCase().startsWith('core') ? 1 : 0;
                      if (aCore !== bCore) return bCore - aCore; // core first
                      const aRel = typeof a?.relevance_score === 'number' ? a.relevance_score : 0;
                      const bRel = typeof b?.relevance_score === 'number' ? b.relevance_score : 0;
                      return bRel - aRel; // higher relevance first
                    });

                    return (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">All Skills Analysis ({sortedSkills.length} skills)</h3>
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proficiency</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Years</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Core/Supporting</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Context</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {sortedSkills.map((skill: any, i: number) => (
                                <tr key={i} className={skill.core_or_supporting?.toLowerCase().startsWith('core') ? 'bg-blue-50' : ''}>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {skill.skill}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                      skill.category === 'Technical' ? 'bg-blue-100 text-blue-800' :
                                      skill.category === 'Programming' ? 'bg-purple-100 text-purple-800' :
                                      skill.category === 'Frameworks' ? 'bg-indigo-100 text-indigo-800' :
                                      skill.category === 'Tools' ? 'bg-gray-100 text-gray-800' :
                                      skill.category === 'Soft Skills' ? 'bg-green-100 text-green-800' :
                                      skill.category === 'Languages' ? 'bg-pink-100 text-pink-800' :
                                      skill.category === 'Methodologies' ? 'bg-orange-100 text-orange-800' :
                                      skill.category === 'Certifications' ? 'bg-yellow-100 text-yellow-800' :
                                      skill.category === 'Emerging' ? 'bg-red-100 text-red-800' :
                                      'bg-teal-100 text-teal-800'
                                    }`}>
                                      {skill.category}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                    <span className={`px-2 py-1 text-xs rounded ${
                                      skill.proficiency === 'Expert' ? 'bg-green-100 text-green-800' :
                                      skill.proficiency === 'Advanced' ? 'bg-blue-100 text-blue-800' :
                                      skill.proficiency === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                      skill.proficiency === 'Basic' || skill.proficiency === 'Beginner' ? 'bg-gray-100 text-gray-800' :
                                      'bg-gray-50 text-gray-600'
                                    }`}>
                                      {skill.proficiency || '-'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{skill.years_experience || '-'}</td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                    <span className={`px-2 py-1 text-xs rounded ${
                                      skill.core_or_supporting?.toLowerCase().startsWith('core') 
                                        ? 'bg-green-100 text-green-800 font-medium' 
                                        : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {skill.core_or_supporting || '-'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate" title={skill.context_demonstrated || '-'}>
                                    {skill.context_demonstrated || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* AI Analysis Markdown */}
                  {resume.ai_analysis?.agent_skills_markdown && (
                    <CollapsibleCard title="Detailed Skills Analysis" defaultOpen>
                      <MarkdownRenderer content={resume.ai_analysis.agent_skills_markdown} />
                    </CollapsibleCard>
                  )}

                  {!resume.analysis?.skills_analysis && !resume.ai_analysis?.agent_skills_markdown && (
                    <div className="text-center py-12 text-gray-500">
                      <CpuChipIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Skills analysis not available</p>
                    </div>
                  )}
                </div>
              )}

              {/* Experience Tab */}
              {activeTab === 'experience' && (
                <div className="space-y-6">
                  {/* Experience Summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-blue-900 mb-4">
                      Experience Summary
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{resume.analysis?.career_analysis?.total_years_experience || resume.total_years_experience || 0}</div>
                        <div className="text-sm text-blue-700">Years Experience</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {resume.analysis?.career_analysis?.leadership_experience ? 'Yes' : 'No'}
                        </div>
                        <div className="text-sm text-blue-700">Leadership Experience</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-blue-600">
                          {resume.analysis?.career_analysis?.current_position || resume.professional_title || 'N/A'}
                        </div>
                        <div className="text-sm text-blue-700">Current Position</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-blue-600">
                          {resume.analysis?.career_analysis?.current_company || 'N/A'}
                        </div>
                        <div className="text-sm text-blue-700">Current Company</div>
                      </div>
                    </div>
                    {resume.analysis?.career_analysis?.career_progression_score && (
                      <div className="mt-4 text-center">
                        <div className="text-sm text-blue-700">Career Progression Score</div>
                        <div className="text-xl font-bold text-blue-600">
                          {Math.round((resume.analysis.career_analysis.career_progression_score || 0) * 100)}%
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Work Experience */}
                  {resume.analysis?.career_analysis?.work_experience && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Work Experience</h3>
                      <div className="space-y-4">
                        {resume.analysis.career_analysis.work_experience.map((exp: any, index: number) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h4 className="font-medium text-gray-900">{exp.position}</h4>
                                <p className="text-sm text-gray-600">{exp.company}</p>
                              </div>
                              <span className="text-sm text-gray-500">
                                {exp.start_date} - {exp.end_date}
                              </span>
                            </div>
                            {exp.description && (
                              <p className="text-sm text-gray-700 mb-3">{exp.description}</p>
                            )}
                            {exp.achievements && exp.achievements.length > 0 && (
                              <div>
                                <h5 className="text-sm font-medium text-gray-900 mb-2">Key Achievements:</h5>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {exp.achievements.map((achievement: string, achIndex: number) => (
                                    <li key={achIndex} className="flex items-start space-x-2">
                                      <span className="text-gray-400 mt-1">•</span>
                                      <span>{achievement}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Analysis Markdown */}
                  {resume.ai_analysis?.agent_experience_markdown && (
                    <CollapsibleCard title="Detailed Experience Analysis" defaultOpen>
                      <MarkdownRenderer content={resume.ai_analysis.agent_experience_markdown} />
                    </CollapsibleCard>
                  )}

                  {!resume.analysis?.career_analysis && !resume.ai_analysis?.agent_experience_markdown && (
                    <div className="text-center py-12 text-gray-500">
                      <BriefcaseIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Experience analysis not available</p>
                    </div>
                  )}
                </div>
              )}

              {/* Education Tab */}
              {activeTab === 'education' && (
                <div className="space-y-6">
                  {/* Education Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-green-900 mb-4">
                      Education Summary
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-green-700">Highest Degree:</span>
                        <div className="font-medium text-green-900">{resume.analysis?.education_analysis?.highest_degree || 'Not specified'}</div>
                      </div>
                      <div>
                        <span className="text-sm text-green-700">Education Relevance:</span>
                        <div className="font-medium text-green-900">
                          {resume.analysis?.education_analysis?.relevant_education ? 'Highly Relevant' : 'Partially Relevant'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Educational Background */}
                  {resume.analysis?.education_analysis?.education && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Educational Background</h3>
                      <div className="space-y-4">
                        {resume.analysis.education_analysis.education.map((edu: any, index: number) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium text-gray-900">{edu.degree}</h4>
                                <p className="text-sm text-gray-600">{edu.institution}</p>
                                {edu.honors && (
                                  <p className="text-sm text-blue-600 font-medium">{edu.honors}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-500">{edu.graduation_date}</div>
                                {edu.gpa && (
                                  <div className="text-sm font-medium text-gray-900">GPA: {edu.gpa}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Analysis Markdown */}
                  {resume.ai_analysis?.agent_education_markdown && (
                    <CollapsibleCard title="Detailed Education Analysis" defaultOpen>
                      <MarkdownRenderer content={resume.ai_analysis.agent_education_markdown} />
                    </CollapsibleCard>
                  )}

                  {!resume.analysis?.education_analysis && !resume.ai_analysis?.agent_education_markdown && (
                    <div className="text-center py-12 text-gray-500">
                      <AcademicCapIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Education analysis not available</p>
                    </div>
                  )}
                </div>
              )}

              {/* Analysis Tab */}
              {activeTab === 'agents' && (
                <div className="space-y-6">
                  {/* Agent Outputs Grid */}
                  <div className="grid grid-cols-1 gap-6">
                    {/* Personal Information Agent */}
                    {resume.agent_personal_info_markdown && (
                      <CollapsibleCard
                        title="Personal Information"
                        subtitle="Extracts contact details and personal information"
                        icon={<UserIcon className="w-5 h-5 text-blue-600" />}
                        defaultOpen={false}
                        headerClassName="bg-blue-50"
                      >
                        <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
                          <MarkdownRenderer 
                            content={resume.agent_personal_info_markdown}
                            className="text-sm"
                          />
                        </div>
                      </CollapsibleCard>
                    )}

                    {/* Experience Agent */}
                    {resume.agent_experience_markdown && (
                      <CollapsibleCard
                        title="Experience Analysis"
                        subtitle="Analyzes work history, career progression, and achievements"
                        icon={<BriefcaseIcon className="w-5 h-5 text-green-600" />}
                        headerClassName="bg-green-50"
                      >
                        <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
                          <MarkdownRenderer 
                            content={resume.agent_experience_markdown}
                            className="text-sm"
                          />
                        </div>
                      </CollapsibleCard>
                    )}

                    {/* Skills Agent */}
                    {resume.agent_skills_markdown && (
                      <CollapsibleCard
                        title="Skills Analysis"
                        subtitle="Identifies and categorizes technical and soft skills with proficiency levels"
                        icon={<CpuChipIcon className="w-5 h-5 text-purple-600" />}
                        headerClassName="bg-purple-50"
                      >
                        <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
                          <MarkdownRenderer 
                            content={resume.agent_skills_markdown}
                            className="text-sm"
                          />
                        </div>
                      </CollapsibleCard>
                    )}

                    {/* Education Agent */}
                    {resume.agent_education_markdown && (
                      <CollapsibleCard
                        title="Education Analysis"
                        subtitle="Analyzes educational background, degrees, and certifications"
                        icon={<AcademicCapIcon className="w-5 h-5 text-indigo-600" />}
                        headerClassName="bg-indigo-50"
                      >
                        <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
                          <MarkdownRenderer 
                            content={resume.agent_education_markdown}
                            className="text-sm"
                          />
                        </div>
                      </CollapsibleCard>
                    )}

                    {/* Quality Assessment Agent */}
                    {resume.agent_quality_markdown && (
                      <CollapsibleCard
                        title="Quality Assessment"
                        subtitle="Evaluates resume quality, completeness, and presentation"
                        icon={<ChartBarIcon className="w-5 h-5 text-yellow-600" />}
                        headerClassName="bg-yellow-50"
                      >
                        <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
                          <MarkdownRenderer 
                            content={resume.agent_quality_markdown}
                            className="text-sm"
                          />
                        </div>
                      </CollapsibleCard>
                    )}

                    {/* AI Analysis Fallback - Show structured data when markdown not available */}
                    {resume.analysis && (
                      <div className="space-y-6">
                        {/* Key Strengths, Areas for Improvement, Recommendations, and AI Reasoning - Top Priority */}
                        {(resume.analysis.quality_assessment?.strengths?.length > 0 || 
                          resume.analysis.quality_assessment?.improvement_areas?.length > 0 || 
                          resume.analysis.quality_assessment?.recommendations?.length > 0 ||
                          resume.analysis.quality_assessment?.ai_reasoning) && (
                          <div className="space-y-6">
                            {/* Key Strengths */}
                            {resume.analysis.quality_assessment.strengths && resume.analysis.quality_assessment.strengths.length > 0 && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                <h3 className="text-lg font-medium text-green-900 mb-4 flex items-center">
                                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                                  Key Strengths
                                </h3>
                                <ul className="space-y-2">
                                  {resume.analysis.quality_assessment.strengths.map((strength: string, index: number) => (
                                    <li key={index} className="flex items-start space-x-2">
                                      <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-green-800">{strength}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Areas for Improvement */}
                            {resume.analysis.quality_assessment.improvement_areas && resume.analysis.quality_assessment.improvement_areas.length > 0 && (
                              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                                <h3 className="text-lg font-medium text-orange-900 mb-4 flex items-center">
                                  <ExclamationTriangleIcon className="w-5 h-5 text-orange-500 mr-2" />
                                  Areas for Improvement
                                </h3>
                                <ul className="space-y-2">
                                  {resume.analysis.quality_assessment.improvement_areas.map((area: string, index: number) => (
                                    <li key={index} className="flex items-start space-x-2">
                                      <ExclamationTriangleIcon className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-orange-800">{area}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Recommendations */}
                            {resume.analysis.quality_assessment.recommendations && resume.analysis.quality_assessment.recommendations.length > 0 && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center">
                                  <CheckCircleIcon className="w-5 h-5 text-blue-500 mr-2" />
                                  Recommendations
                                </h3>
                                <ul className="space-y-2">
                                  {resume.analysis.quality_assessment.recommendations.map((rec: string, index: number) => (
                                    <li key={index} className="flex items-start space-x-2">
                                      <CheckCircleIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-blue-800">{rec}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* AI Analysis Reasoning */}
                            {resume.analysis.quality_assessment.ai_reasoning && (
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                                <h3 className="text-lg font-medium text-purple-900 mb-4 flex items-center">
                                  <BeakerIcon className="w-5 h-5 text-purple-500 mr-2" />
                                  AI Analysis Reasoning
                                </h3>
                                <p className="text-purple-800 leading-relaxed">
                                  {resume.analysis.quality_assessment.ai_reasoning}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Personal Information Analysis */}
                        {resume.analysis.personal_info && (
                          <CollapsibleCard
                            title="Personal Information Analysis"
                            subtitle="AI-extracted personal details and contact information"
                            icon={<UserIcon className="w-5 h-5 text-blue-600" />}
                            defaultOpen={true}
                            headerClassName="bg-blue-50"
                          >
                            <div className="bg-white rounded-lg p-6 space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {resume.analysis.personal_info.full_name && (
                                  <div className="flex items-center space-x-3">
                                    <UserIcon className="w-5 h-5 text-blue-500" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">Full Name</p>
                                      <p className="text-sm text-gray-600">{resume.analysis.personal_info.full_name}</p>
                                    </div>
                                  </div>
                                )}
                                {resume.analysis.personal_info.email && (
                                  <div className="flex items-center space-x-3">
                                    <EnvelopeIcon className="w-5 h-5 text-blue-500" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">Email</p>
                                      <p className="text-sm text-gray-600">{resume.analysis.personal_info.email}</p>
                                    </div>
                                  </div>
                                )}
                                {resume.analysis.personal_info.phone && (
                                  <div className="flex items-center space-x-3">
                                    <PhoneIcon className="w-5 h-5 text-blue-500" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">Phone</p>
                                      <p className="text-sm text-gray-600">{resume.analysis.personal_info.phone}</p>
                                    </div>
                                  </div>
                                )}
                                {resume.analysis.personal_info.location && (
                                  <div className="flex items-center space-x-3">
                                    <MapPinIcon className="w-5 h-5 text-blue-500" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">Location</p>
                                      <p className="text-sm text-gray-600">{resume.analysis.personal_info.location}</p>
                                    </div>
                                  </div>
                                )}
                                {resume.analysis.personal_info.professional_title && (
                                  <div className="flex items-center space-x-3 md:col-span-2">
                                    <BriefcaseIcon className="w-5 h-5 text-blue-500" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">Professional Title</p>
                                      <p className="text-sm text-gray-600">{resume.analysis.personal_info.professional_title}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Social Links */}
                              {(resume.analysis.personal_info.linkedin_url || resume.analysis.personal_info.github_url || resume.analysis.personal_info.portfolio_url) && (
                                <div className="border-t pt-4">
                                  <h4 className="text-sm font-medium text-gray-900 mb-3">Social Links</h4>
                                  <div className="flex flex-wrap gap-3">
                                    {resume.analysis.personal_info.linkedin_url && (
                                      <a href={resume.analysis.personal_info.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-blue-600 hover:text-blue-800">
                                        <span className="text-sm">LinkedIn</span>
                                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                      </a>
                                    )}
                                    {resume.analysis.personal_info.github_url && (
                                      <a href={resume.analysis.personal_info.github_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-600 hover:text-gray-800">
                                        <span className="text-sm">GitHub</span>
                                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                      </a>
                                    )}
                                    {resume.analysis.personal_info.portfolio_url && (
                                      <a href={resume.analysis.personal_info.portfolio_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-purple-600 hover:text-purple-800">
                                        <span className="text-sm">Portfolio</span>
                                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                      </a>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </CollapsibleCard>
                        )}

                        {/* Skills Analysis */}
                        {resume.analysis?.skills_analysis && (
                          <CollapsibleCard
                            title="Skills Analysis"
                            subtitle="AI-powered skills extraction and categorization"
                            icon={<CpuChipIcon className="w-5 h-5 text-purple-600" />}
                            defaultOpen={false}
                            headerClassName="bg-purple-50"
                          >
                            <div className="bg-white rounded-lg p-6 space-y-6">
                              {/* Technical Skills */}
                              {resume.analysis.skills_analysis.technical_skills && resume.analysis.skills_analysis.technical_skills.length > 0 && (
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                    <CpuChipIcon className="w-5 h-5 text-purple-500 mr-2" />
                                    Technical Skills
                                  </h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {resume.analysis.skills_analysis.technical_skills.map((skill: any, index: number) => (
                                      <div key={index} className="bg-purple-50 rounded-lg p-3">
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-gray-900">{skill.skill || skill}</span>
                                          <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full">
                                            {skill.proficiency || 'Unknown'}
                                          </span>
                                        </div>
                                        {skill.years_experience && (
                                          <p className="text-sm text-gray-600 mt-1">{skill.years_experience} years experience</p>
                                        )}
                                        {skill.context_demonstrated && (
                                          <p className="text-xs text-gray-500 mt-1">{skill.context_demonstrated}</p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Soft Skills */}
                              {resume.analysis.skills_analysis.soft_skills && resume.analysis.skills_analysis.soft_skills.length > 0 && (
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                    <UserGroupIcon className="w-5 h-5 text-green-500 mr-2" />
                                    Soft Skills
                                  </h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {resume.analysis.skills_analysis.soft_skills.map((skill: any, index: number) => (
                                      <div key={index} className="bg-green-50 rounded-lg p-3">
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-gray-900">{skill.skill || skill}</span>
                                          <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full">
                                            {skill.proficiency || 'Unknown'}
                                          </span>
                                        </div>
                                        {skill.years_experience && (
                                          <p className="text-sm text-gray-600 mt-1">{skill.years_experience} years experience</p>
                                        )}
                                        {skill.context_demonstrated && (
                                          <p className="text-xs text-gray-500 mt-1">{skill.context_demonstrated}</p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Methodologies */}
                              {resume.analysis.skills_analysis.methodologies && resume.analysis.skills_analysis.methodologies.length > 0 && (
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                    <CogIcon className="w-5 h-5 text-blue-500 mr-2" />
                                    Methodologies
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {resume.analysis.skills_analysis.methodologies.map((skill: any, index: number) => (
                                      <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {skill.skill || skill}
                                        {skill.proficiency && (
                                          <span className="ml-2 text-xs bg-blue-200 px-2 py-0.5 rounded-full">
                                            {skill.proficiency}
                                          </span>
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Tools & Technologies */}
                              {resume.analysis.skills_analysis.tools_technologies && resume.analysis.skills_analysis.tools_technologies.length > 0 && (
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                    <WrenchScrewdriverIcon className="w-5 h-5 text-orange-500 mr-2" />
                                    Tools & Technologies
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {resume.analysis.skills_analysis.tools_technologies.map((skill: any, index: number) => (
                                      <span key={index} className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {skill.skill || skill}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </CollapsibleCard>
                        )}

                        {/* Career Analysis */}
                        {resume.analysis?.career_analysis && (
                          <CollapsibleCard
                            title="Career Analysis"
                            subtitle="Professional experience and career progression"
                            icon={<BriefcaseIcon className="w-5 h-5 text-green-600" />}
                            defaultOpen={false}
                            headerClassName="bg-green-50"
                          >
                            <div className="bg-white rounded-lg p-6 space-y-6">
                              {/* Experience Summary */}
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center space-x-2">
                                  <ClockIcon className="w-5 h-5 text-green-600" />
                                  <span className="font-medium text-green-900">Total Experience</span>
                                  <span className="bg-green-200 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                                    {resume.analysis.career_analysis.total_years_experience || 0} years
                                  </span>
                                </div>
                              </div>

                              {/* Leadership Experience */}
                              {resume.analysis.career_analysis.leadership_experience && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                  <div className="flex items-center space-x-2">
                                    <UserGroupIcon className="w-5 h-5 text-yellow-600" />
                                    <span className="font-medium text-yellow-900">Leadership Experience</span>
                                    <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">Yes</span>
                                  </div>
                                </div>
                              )}

                              {/* Career Progression Score */}
                              {resume.analysis.career_analysis.career_progression_score && (
                                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <ChartBarIcon className="w-5 h-5 text-indigo-600" />
                                      <span className="font-medium text-indigo-900">Career Progression Score</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <div className="w-24 bg-gray-200 rounded-full h-2">
                                        <div 
                                          className="bg-indigo-600 h-2 rounded-full" 
                                          style={{ width: `${(resume.analysis.career_analysis.career_progression_score * 100)}%` }}
                                        ></div>
                                      </div>
                                      <span className="text-sm font-medium text-indigo-900">
                                        {Math.round(resume.analysis.career_analysis.career_progression_score * 100)}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Work Experience */}
                              {resume.analysis.career_analysis.work_experience && resume.analysis.career_analysis.work_experience.length > 0 && (
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                    <BriefcaseIcon className="w-5 h-5 text-green-500 mr-2" />
                                    Work Experience
                                  </h4>
                                  <div className="space-y-4">
                                    {resume.analysis.career_analysis.work_experience.map((job: any, index: number) => (
                                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between mb-2">
                                          <div>
                                            <h5 className="font-semibold text-gray-900">{job.position}</h5>
                                            <p className="text-sm text-gray-600">{job.company}</p>
                                            <p className="text-xs text-gray-500">{job.location}</p>
                                          </div>
                                          <div className="text-right">
                                            <p className="text-sm font-medium text-gray-900">{job.duration}</p>
                                            <p className="text-xs text-gray-500">{job.start_date} - {job.end_date}</p>
                                          </div>
                                        </div>
                                        
                                        {job.responsibilities && job.responsibilities.length > 0 && (
                                          <div className="mt-3">
                                            <h6 className="text-sm font-medium text-gray-700 mb-2">Key Responsibilities:</h6>
                                            <ul className="text-sm text-gray-600 space-y-1">
                                              {job.responsibilities.map((resp: string, respIndex: number) => (
                                                <li key={respIndex} className="flex items-start space-x-2">
                                                  <span className="text-green-500 mt-1">•</span>
                                                  <span>{resp}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}

                                        {job.achievements && job.achievements.length > 0 && (
                                          <div className="mt-3">
                                            <h6 className="text-sm font-medium text-gray-700 mb-2">Key Achievements:</h6>
                                            <ul className="text-sm text-gray-600 space-y-1">
                                              {job.achievements.map((achievement: string, achIndex: number) => (
                                                <li key={achIndex} className="flex items-start space-x-2">
                                                  <span className="text-blue-500 mt-1">•</span>
                                                  <span>{achievement}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}

                                        {job.technologies_used && job.technologies_used.length > 0 && (
                                          <div className="mt-3">
                                            <h6 className="text-sm font-medium text-gray-700 mb-2">Technologies Used:</h6>
                                            <div className="flex flex-wrap gap-1">
                                              {job.technologies_used.map((tech: string, techIndex: number) => (
                                                <span key={techIndex} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                                  {tech}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </CollapsibleCard>
                        )}

                        {/* Education Analysis */}
                        {resume.analysis?.education_analysis && (
                          <CollapsibleCard
                            title="Education Analysis"
                            subtitle="Educational background and qualifications"
                            icon={<AcademicCapIcon className="w-5 h-5 text-indigo-600" />}
                            defaultOpen={false}
                            headerClassName="bg-indigo-50"
                          >
                            <div className="bg-white rounded-lg p-6 space-y-6">
                              {/* Education Summary */}
                              {resume.analysis.education_analysis.highest_degree && (
                                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                  <div className="flex items-center space-x-2">
                                    <AcademicCapIcon className="w-5 h-5 text-indigo-600" />
                                    <span className="font-medium text-indigo-900">Highest Degree</span>
                                    <span className="bg-indigo-200 text-indigo-800 px-2 py-1 rounded-full text-sm font-medium">
                                      {resume.analysis.education_analysis.highest_degree}
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* Education Details */}
                              {resume.analysis.education_analysis.education && resume.analysis.education_analysis.education.length > 0 && (
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                    <AcademicCapIcon className="w-5 h-5 text-indigo-500 mr-2" />
                                    Education Details
                                  </h4>
                                  <div className="space-y-4">
                                    {resume.analysis.education_analysis.education.map((edu: any, index: number) => (
                                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between mb-2">
                                          <div>
                                            <h5 className="font-semibold text-gray-900">{edu.degree}</h5>
                                            <p className="text-sm text-gray-600">{edu.institution}</p>
                                            <p className="text-xs text-gray-500">{edu.location}</p>
                                            {edu.field_of_study && (
                                              <p className="text-xs text-gray-500">Field: {edu.field_of_study}</p>
                                            )}
                                          </div>
                                          <div className="text-right">
                                            {edu.graduation_year && (
                                              <p className="text-sm font-medium text-gray-900">{edu.graduation_year}</p>
                                            )}
                                            {edu.gpa && (
                                              <p className="text-xs text-gray-500">GPA: {edu.gpa}</p>
                                            )}
                                          </div>
                                        </div>
                                        {edu.honors && (
                                          <div className="mt-2">
                                            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                                              {edu.honors}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Certifications */}
                              {resume.analysis.education_analysis.certifications && resume.analysis.education_analysis.certifications.length > 0 && (
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                    <DocumentTextIcon className="w-5 h-5 text-green-500 mr-2" />
                                    Certifications
                                  </h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {resume.analysis.education_analysis.certifications.map((cert: any, index: number) => (
                                      <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                                        <div className="flex items-start justify-between">
                                          <div>
                                            <h6 className="font-medium text-gray-900">{cert.name}</h6>
                                            {cert.issuer && (
                                              <p className="text-sm text-gray-600">{cert.issuer}</p>
                                            )}
                                            {cert.date_earned && (
                                              <p className="text-xs text-gray-500">Earned: {cert.date_earned}</p>
                                            )}
                                            {cert.expiration_date && (
                                              <p className="text-xs text-gray-500">Expires: {cert.expiration_date}</p>
                                            )}
                                          </div>
                                          {cert.credential_id && (
                                            <span className="bg-green-200 text-green-800 px-2 py-1 rounded text-xs font-mono">
                                              {cert.credential_id}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </CollapsibleCard>
                        )}

                        {/* Quality Assessment */}
                        {resume.analysis?.quality_assessment && (
                          <CollapsibleCard
                            title="Quality Assessment"
                            subtitle="Resume quality metrics and evaluation"
                            icon={<ChartBarIcon className="w-5 h-5 text-yellow-600" />}
                            defaultOpen={false}
                            headerClassName="bg-yellow-50"
                          >
                            <div className="bg-white rounded-lg p-6 space-y-6">
                              {/* Overall Score */}
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <ChartBarIcon className="w-5 h-5 text-yellow-600" />
                                    <span className="font-medium text-yellow-900">Overall Quality Score</span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div className="w-32 bg-gray-200 rounded-full h-3">
                                      <div 
                                        className="bg-yellow-600 h-3 rounded-full" 
                                        style={{ width: `${(resume.analysis.quality_assessment.overall_score || 0) * 100}%` }}
                                      ></div>
                                    </div>
                                    <span className="text-lg font-bold text-yellow-900">
                                      {Math.round((resume.analysis.quality_assessment.overall_score || 0) * 100)}%
                                    </span>
                                  </div>
                                </div>
                                <div className="mt-2">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    (resume.analysis.quality_assessment.overall_score || 0) >= 0.8 
                                      ? 'bg-green-100 text-green-800' 
                                      : (resume.analysis.quality_assessment.overall_score || 0) >= 0.6 
                                        ? 'bg-yellow-100 text-yellow-800' 
                                        : 'bg-red-100 text-red-800'
                                  }`}>
                                    Grade: {resume.analysis.quality_assessment.grade || 'N/A'}
                                  </span>
                                </div>
                              </div>

                              {/* Quality Scores Breakdown */}
                              {resume.analysis.quality_assessment.quality_scores && (
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900 mb-4">Quality Breakdown</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {Object.entries(resume.analysis.quality_assessment.quality_scores).map(([key, value]) => (
                                      <div key={key} className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-sm font-medium text-gray-700 capitalize">
                                            {key.replace('_', ' ')}
                                          </span>
                                          <span className="text-sm font-bold text-gray-900">
                                            {Math.round((value as number) * 100)}%
                                          </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                          <div 
                                            className="bg-blue-600 h-2 rounded-full" 
                                            style={{ width: `${(value as number) * 100}%` }}
                                          ></div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </CollapsibleCard>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <ExclamationCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No resume data available</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Job Form component (modal)
const JobForm: React.FC<{ 
  job?: Job | null, 
  onSubmit: (data: JobFormData) => void,
  onCancel: () => void,
  isLoading: boolean 
}> = ({ job, onSubmit, onCancel, isLoading }) => {
  
  // Use useMemo to calculate form data when job changes
  const initialFormData = React.useMemo(() => {
    const formData = {
      title: job?.title || '',
      department: job?.department || '',
      client: job?.client || '',
      source_id: job?.source_id || undefined,
      location: job?.location || '',
      employment_type: job?.employment_type || 'full-time',
      remote_option: job?.remote_option || 'no',
      description: job?.description || '',
      responsibilities: job?.responsibilities || '',
      requirements: job?.requirements || '',
      benefits: job?.benefits || '',
      salary_min: job?.salary_min,
      salary_max: job?.salary_max,
      salary_currency: job?.salary_currency || 'USD',
      salary_period: job?.salary_period || 'annual',
      application_deadline: job?.application_deadline,
      min_years_experience: job?.min_years_experience || undefined,
      max_years_experience: job?.max_years_experience || undefined
    }
    return formData
  }, [job])

  const [formData, setFormData] = useState<JobFormData>(initialFormData)
  const { user } = useAuth()
  const [selectedAssignees, setSelectedAssignees] = useState<OrganizationUser[]>(() => {
    if (job?.assigned_recruiters && job.assigned_recruiters.length > 0) {
      return job.assigned_recruiters.map(r => ({
        id: r.id,
        full_name: r.name,
        email: r.email,
        role: r.role
      }))
    }
    if (user) {
      return [{ id: user.id, full_name: user.fullName, email: user.email, role: user.role }]
    }
    return []
  })
  const [assigneeError, setAssigneeError] = useState<string>('')
  
  // Update form data when switching between different jobs
  React.useEffect(() => {
    setFormData(initialFormData)
  }, [job?.id, initialFormData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate assignees
    if (selectedAssignees.length === 0) {
      setAssigneeError('At least one assignee is required')
      return
    }
    
    // Validate that there are no empty items (empty strings) in fields that have content
    const hasEmptyResponsibilities = formData.responsibilities && formData.responsibilities.trim() && formData.responsibilities.split('\n').some(item => item.trim() === '')
    const hasEmptyRequirements = formData.requirements && formData.requirements.trim() && formData.requirements.split('\n').some(item => item.trim() === '')
    const hasEmptyBenefits = formData.benefits && formData.benefits.trim() && formData.benefits.split('\n').some(item => item.trim() === '')
    
    if (hasEmptyResponsibilities || hasEmptyRequirements || hasEmptyBenefits) {
      const emptySections = []
      if (hasEmptyResponsibilities) emptySections.push('Responsibilities')
      if (hasEmptyRequirements) emptySections.push('Requirements')
      if (hasEmptyBenefits) emptySections.push('Benefits')
      
      toast.error(`Please complete or remove the empty items in: ${emptySections.join(', ')}`)
      return
    }
    
    const submitData: JobFormData = {
      ...formData,
      assigned_user_ids: selectedAssignees.map(u => u.id)
    }
    
    onSubmit(submitData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Not `sticky` inside the scrollable body below — a header sticky
            within the same scroll container as the fields can end up
            painting in the same band as whatever field has scrolled to the
            top (browser/zoom-dependent), looking like the header and a
            field are overlapping. Taking the header fully out of the
            scrolling region avoids that class of bug entirely. */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Edit Job Posting
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.department || ''}
                onChange={(e) => setFormData({...formData, department: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.client ?? ''}
                onChange={(e) => setFormData({...formData, client: e.target.value})}
                placeholder="Client name"
              />
            </div>
            <div>
              <SourceSelector
                type="job"
                value={formData.source_id || null}
                onChange={(sourceId) => setFormData({...formData, source_id: sourceId})}
                required={true}
                label="Source"
              />
            </div>
            <div>
              <AssigneeSelector
                selectedAssignees={selectedAssignees}
                onChange={(assignees) => {
                  setSelectedAssignees(assignees)
                  if (assignees.length > 0) setAssigneeError('')
                }}
                currentUser={user ? { id: user.id, full_name: user.fullName, email: user.email, role: user.role } : { id: '', full_name: '', email: '', role: '' }}
                error={assigneeError}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.location || ''}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type *</label>
              <select
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.employment_type}
                onChange={(e) => setFormData({...formData, employment_type: e.target.value})}
              >
                <option value="full-time">Full Time</option>
                <option value="part-time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="freelance">Freelance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remote Option *</label>
              <select
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.remote_option}
                onChange={(e) => setFormData({...formData, remote_option: e.target.value})}
              >
                <option value="no">On Site</option>
                <option value="hybrid">Hybrid</option>
                <option value="full">Fully Remote</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Description *</label>
            <textarea
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Describe the role and responsibilities..."
            />
          </div>

          {/* Responsibilities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Responsibilities</label>
            <div className="p-3 bg-gray-50 rounded-lg border min-h-[120px]">
              <div className="text-xs text-gray-600 mb-2 font-medium">Responsibilities (click to edit):</div>
              <ul className="space-y-2">
                {formData.responsibilities !== undefined ? (
                  formData.responsibilities.split('\n').map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-gray-700 group">
                    <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                    <textarea
                      value={item}
                      onChange={(e) => {
                        const lines = (formData.responsibilities || '').split('\n')
                        lines[index] = e.target.value
                        setFormData({...formData, responsibilities: lines.join('\n')})
                      }}
                      className="flex-1 bg-transparent border-none outline-none focus:bg-white focus:border focus:border-blue-300 focus:px-2 focus:py-1 focus:rounded resize-none w-full leading-relaxed"
                      placeholder="Enter responsibility..."
                      rows={1}
                      style={{ 
                        height: 'auto',
                        minHeight: '24px',
                        lineHeight: '1.5',
                        overflow: 'hidden',
                        wordWrap: 'break-word'
                      }}
                      ref={(textarea) => {
                        if (textarea) {
                          textarea.style.height = 'auto'
                          textarea.style.height = textarea.scrollHeight + 'px'
                        }
                      }}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement
                        target.style.height = 'auto'
                        target.style.height = target.scrollHeight + 'px'
                      }}
                      onFocus={(e) => {
                        const target = e.target as HTMLTextAreaElement
                        target.style.height = 'auto'
                        target.style.height = target.scrollHeight + 'px'
                      }}
                    />
                      <button
                        type="button"
                        onClick={() => {
                          const lines = (formData.responsibilities || '').split('\n').filter((_, i) => i !== index)
                          const newResponsibilities = lines.join('\n')
                          // If no lines left or only empty lines, set to undefined
                          if (newResponsibilities.trim() === '' || lines.length === 0) {
                            setFormData({...formData, responsibilities: undefined})
                          } else {
                            setFormData({...formData, responsibilities: newResponsibilities})
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 text-xs"
                      >
                        ✕
                      </button>
                  </li>
                  ))
                ) : (
                  <li className="text-gray-400 text-sm italic">No responsibilities added yet</li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => {
                  const currentResponsibilities = formData.responsibilities || ''
                  
                  // If responsibilities are empty, add one empty responsibility
                  // If responsibilities exist, add a new empty responsibility
                  let newResponsibilities
                  if (currentResponsibilities.trim().length === 0) {
                    newResponsibilities = '' // Start with empty string for first responsibility
                  } else {
                    newResponsibilities = `${currentResponsibilities}\n` // Add newline for additional responsibility
                  }
                  
                  setFormData({...formData, responsibilities: newResponsibilities})
                }}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add responsibility
              </button>
            </div>
          </div>

          {/* Requirements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label>
            <div className="p-3 bg-gray-50 rounded-lg border min-h-[120px]">
              <div className="text-xs text-gray-600 mb-2 font-medium">Requirements (click to edit):</div>
              <ul className="space-y-2">
                {formData.requirements !== undefined ? (
                  formData.requirements.split('\n').map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-gray-700 group">
                    <span className="text-green-500 mt-1 flex-shrink-0">•</span>
                    <textarea
                      value={item}
                      onChange={(e) => {
                        const lines = (formData.requirements || '').split('\n')
                        lines[index] = e.target.value
                        setFormData({...formData, requirements: lines.join('\n')})
                      }}
                      className="flex-1 bg-transparent border-none outline-none focus:bg-white focus:border focus:border-green-300 focus:px-2 focus:py-1 focus:rounded resize-none w-full leading-relaxed"
                      placeholder="Enter requirement..."
                      rows={1}
                      style={{ 
                        height: 'auto',
                        minHeight: '24px',
                        lineHeight: '1.5',
                        overflow: 'hidden',
                        wordWrap: 'break-word'
                      }}
                      ref={(textarea) => {
                        if (textarea) {
                          textarea.style.height = 'auto'
                          textarea.style.height = textarea.scrollHeight + 'px'
                        }
                      }}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement
                        target.style.height = 'auto'
                        target.style.height = target.scrollHeight + 'px'
                      }}
                      onFocus={(e) => {
                        const target = e.target as HTMLTextAreaElement
                        target.style.height = 'auto'
                        target.style.height = target.scrollHeight + 'px'
                      }}
                    />
                      <button
                        type="button"
                        onClick={() => {
                          const lines = (formData.requirements || '').split('\n').filter((_, i) => i !== index)
                          const newRequirements = lines.join('\n')
                          // If no lines left or only empty lines, set to undefined
                          if (newRequirements.trim() === '' || lines.length === 0) {
                            setFormData({...formData, requirements: undefined})
                          } else {
                            setFormData({...formData, requirements: newRequirements})
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 text-xs"
                      >
                        ✕
                      </button>
                  </li>
                  ))
                ) : (
                  <li className="text-gray-400 text-sm italic">No requirements added yet</li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => {
                  const currentRequirements = formData.requirements || ''
                  
                  // If requirements are empty, add one empty requirement
                  // If requirements exist, add a new empty requirement
                  let newRequirements
                  if (currentRequirements.trim().length === 0) {
                    newRequirements = '' // Start with empty string for first requirement
                  } else {
                    newRequirements = `${currentRequirements}\n` // Add newline for additional requirement
                  }
                  
                  setFormData({...formData, requirements: newRequirements})
                }}
                className="mt-2 text-xs text-green-600 hover:text-green-800 font-medium"
              >
                + Add requirement
              </button>
            </div>
          </div>

          {/* Benefits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Benefits</label>
            <div className="p-3 bg-gray-50 rounded-lg border min-h-[120px]">
              <div className="text-xs text-gray-600 mb-2 font-medium">Benefits (click to edit):</div>
              <ul className="space-y-2">
                {formData.benefits !== undefined ? (
                  (formData.benefits || '').split('\n').map((item, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm text-gray-700 group">
                      <span className="text-purple-500 mt-1 flex-shrink-0">•</span>
                      <textarea
                        value={item}
                        onChange={(e) => {
                          const lines = (formData.benefits || '').split('\n')
                          lines[index] = e.target.value
                          setFormData({...formData, benefits: lines.join('\n')})
                        }}
                        className="flex-1 bg-transparent border-none outline-none focus:bg-white focus:border focus:border-purple-300 focus:px-2 focus:py-1 focus:rounded resize-none w-full leading-relaxed"
                        placeholder="Enter benefit..."
                        rows={1}
                        style={{ 
                          height: 'auto',
                          minHeight: '24px',
                          lineHeight: '1.5',
                          overflow: 'hidden',
                          wordWrap: 'break-word'
                        }}
                        ref={(textarea) => {
                          if (textarea) {
                            textarea.style.height = 'auto'
                            textarea.style.height = textarea.scrollHeight + 'px'
                          }
                        }}
                        onInput={(e) => {
                          const target = e.target as HTMLTextAreaElement
                          target.style.height = 'auto'
                          target.style.height = target.scrollHeight + 'px'
                        }}
                        onFocus={(e) => {
                          const target = e.target as HTMLTextAreaElement
                          target.style.height = 'auto'
                          target.style.height = target.scrollHeight + 'px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const lines = (formData.benefits || '').split('\n').filter((_, i) => i !== index)
                          const newBenefits = lines.join('\n')
                          // If no lines left or only empty lines, set to undefined
                          if (newBenefits.trim() === '' || lines.length === 0) {
                            setFormData({...formData, benefits: undefined})
                          } else {
                            setFormData({...formData, benefits: newBenefits})
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 text-xs"
                      >
                        ✕
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="text-gray-400 text-sm italic">No benefits added yet</li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => {
                  const currentBenefits = formData.benefits || ''
                  
                  // If benefits are empty, add one empty benefit
                  // If benefits exist, add a new empty benefit
                  let newBenefits
                  if (currentBenefits.trim().length === 0) {
                    newBenefits = '' // Start with empty string for first benefit
                  } else {
                    newBenefits = `${currentBenefits}\n` // Add newline for additional benefit
                  }
                  
                  setFormData({...formData, benefits: newBenefits})
                }}
                className="mt-2 text-xs text-purple-600 hover:text-purple-800 font-medium"
              >
                + Add benefit
              </button>
            </div>
          </div>

          {/* Years of Experience */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.min_years_experience ?? ''}
                  onChange={(e) => setFormData({...formData, min_years_experience: e.target.value ? parseInt(e.target.value) : undefined})}
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Max"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.max_years_experience ?? ''}
                  onChange={(e) => setFormData({...formData, max_years_experience: e.target.value ? parseInt(e.target.value) : undefined})}
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Salary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
            <div className="flex space-x-2">
              <input
                type="number"
                step="0.01"
                placeholder="Min"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.salary_min || ''}
                onChange={(e) => setFormData({...formData, salary_min: e.target.value ? parseFloat(e.target.value) : undefined})}
              />
              <input
                type="number"
                step="0.01"
                placeholder="Max"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.salary_max || ''}
                onChange={(e) => setFormData({...formData, salary_max: e.target.value ? parseFloat(e.target.value) : undefined})}
              />
            </div>
          </div>

          {/* Salary Period and Currency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.salary_period}
                onChange={(e) => setFormData({...formData, salary_period: e.target.value})}
              >
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
                <option value="hourly">Hourly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.salary_currency}
                onChange={(e) => setFormData({...formData, salary_currency: e.target.value})}
              >
                <option value="USD">USD</option>
                <option value="INR">INR</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <span>Update Job</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Matching-status polling: interval between checks, and a hard cap on how many
// checks a single query instance will run before giving up. The cap exists so a
// candidate stuck at "processing" forever (a crashed worker, a task that never
// completes) can't poll indefinitely — it resets naturally whenever the query key
// changes (new job, tab remount).
const MATCH_POLL_INTERVAL_MS = 5000
const MATCH_POLL_MAX_ATTEMPTS = 120 // ~10 minutes at 5s each

const JobDetailPage: React.FC = () => {
  const { id: jobId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  
  // Active tab state + persistence (URL + localStorage)
  const [activeTab, setActiveTab] = useState<'overview' | 'skills' | 'experience' | 'matching' | 'voiceScreening' | 'interview' | 'proctoredInterview' | 'liveInterview'>('overview')

  // The tab bar scrolls sideways when the tabs don't fit; keep the selected
  // tab visible (e.g. opening the page on ?tab=liveInterview).
  const activeTabButtonRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    if (activeTabButtonRef.current) {
      activeTabButtonRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
  }, [activeTab])

  // Resolve tab on mount from the URL param only. Removed the localStorage
  // fallback 2026-10-08 — Job Management's "eye" icon (JobsPage.tsx) links
  // to a bare /jobs/:id with no ?tab=, so that fallback was restoring
  // whatever tab this job was last viewed on instead of landing on the
  // first tab (Job Overview/Overview) every fresh visit is supposed to
  // open on. The tab click handler below always sets this query param, so
  // refreshing mid-tab or sharing/bookmarking a deep link (?tab=interview)
  // both still land correctly without it.
  useEffect(() => {
    const validTabs = ['overview', 'skills', 'experience', 'matching', 'interview', 'proctoredInterview', 'liveInterview'] as const
    const urlTab = new URLSearchParams(window.location.search).get('tab')
    if (urlTab && (validTabs as readonly string[]).includes(urlTab)) {
      setActiveTab(urlTab as typeof validTabs[number])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  // Keep state in sync if URL params change (back/forward navigation)
  useEffect(() => {
    const tab = searchParams.get('tab')
    const validTabs = ['overview', 'skills', 'experience', 'matching', 'interview', 'proctoredInterview', 'liveInterview'] as const
    if (tab && (validTabs as readonly string[]).includes(tab)) {
      setActiveTab(tab as typeof validTabs[number])
    }
  }, [searchParams, jobId])
  
  // Matching state
  const [searchTerm, setSearchTerm] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([])
  const [showComparison, setShowComparison] = useState(false)
  const [comparisonResults, setComparisonResults] = useState<any>(null)
  const [comparisonLoading, setComparisonLoading] = useState(false)
  const [selectedCandidateForDetails, setSelectedCandidateForDetails] = useState<string | null>(null)
  const [reprocessingRecords, setReprocessingRecords] = useState<Set<string>>(new Set())

  // Filter state for matching tab
  const {
    filterState,
    setFilterState,
    filteredMatches,
    uniqueUsers,
    uniqueStatuses,
    clearFilters,
    togglePanel,
    hasActiveFilters: hasFilters,
    activeFilterCount
  } = useMatchFilters(comparisonResults?.comparison_results || [])

  // Master candidate-status list — used to resolve a status id to its name/stage
  // when optimistically updating a match, so the Status filter stays consistent.
  const { statusById: candidateStatusById } = useCandidateStages()
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredMatches.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedMatches = filteredMatches.slice(startIndex, endIndex)
  
  // Debug logging
  console.log('Pagination Debug:', {
    filteredMatchesLength: filteredMatches.length,
    itemsPerPage,
    totalPages,
    currentPage,
    paginatedMatchesLength: paginatedMatches.length,
    shouldShowPagination: filteredMatches.length > itemsPerPage
  })
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filteredMatches.length])
  
  // Application status state
  const [applicationStatuses, setApplicationStatuses] = useState<Record<string, string>>({})
  
  // Interview state
  const [showInterviewModal, setShowInterviewModal] = useState(false)
  const [interviewGuide, setInterviewGuide] = useState<ExtendedInterviewGuide | null>(null)
  const [interviewLoading, setInterviewLoading] = useState(false)
  const [l1Loading, setL1Loading] = useState<string | null>(null)
  const [l2Loading, setL2Loading] = useState<string | null>(null)
  const [interviewReprocessing, setInterviewReprocessing] = useState(false)
  const [selectedCandidateForInterview, setSelectedCandidateForInterview] = useState<string | null>(null)
  const [showInterviewDropdown, setShowInterviewDropdown] = useState<string | null>(null) 
  const [showCandidateActionsDropdown, setShowCandidateActionsDropdown] = useState<string | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<'above' | 'below'>('below')
  const [buttonPosition, setButtonPosition] = useState<{ left: number; top: number; bottom: number } | null>(null)
  
  // Upload resume state
  const [uploadingResume, setUploadingResume] = useState(false)
  const [showUploadSection, setShowUploadSection] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [resumeSourceId, setResumeSourceId] = useState<string | null>(null)
  // Tracks post-upload background processing stage shown inside the upload section
  const [uploadMatchState, setUploadMatchState] = useState<{ stage: 'analyzing' | 'matching', candidateName: string } | null>(null)
  // Bulk apply is hosted app-wide by BulkApplyProvider, so a running batch
  // keeps going (and stays visible) if the recruiter navigates away.
  const { openBulkApply } = useBulkApply()

  // Duplicate detection state
  const [duplicateData, setDuplicateData] = useState<DuplicateDetectionData | null>(null)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  
  // Resume popup state
  const [showResumePopup, setShowResumePopup] = useState(false)
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null)
  const [selectedCandidateForProfile, setSelectedCandidateForProfile] = useState<string | null>(null)
  
  // Assignment tracking state
  const [showAssignmentEdit, setShowAssignmentEdit] = useState(false)
  const [showAuditTrail, setShowAuditTrail] = useState(false)
  const [selectedAssignment, setSelectedAssignment] = useState<{
    entityType: 'resume_upload' | 'job_match'
    entityId: string
    entityTitle: string
    currentUserId: string | null
    currentUserName: string | null
  } | null>(null)
  
  // Check user roles for permissions
  const { can, isBypassAll, role } = usePermission()
  // Split 2026-08-14 (§10.1) — these three previously shared one `isAdmin`
  // flag but gate different things: the reprocess/"Reanalyze" action maps
  // to jobs.reprocess (senior_recruiter+, a deliberate widening — the
  // backend has always allowed this tier, the UI just under-showed it);
  // the interview-delete button (via InterviewTab's isAdmin prop) maps to
  // interviews.delete; the Quality Assessment/Matching Configuration
  // panels below have no backend enforcement at all, so they stay on a
  // plain hierarchy check rather than a capability that doesn't exist.
  const canReprocessJob = can('jobs.reprocess')
  const canDeleteInterview = can('interviews.delete')
  // Added 2026-10-07 — InterviewListView's Preview & Edit Questions, Retry
  // Processing, and Reanalyze actions (all content-generation/reprocessing)
  // had no capability gate at all; threaded through InterviewTab the same
  // way canDeleteInterview already is via the isAdmin prop. Start Interview
  // maps to interviews.create instead (continuing the lifecycle of an
  // interview this role is allowed to create) — see RoleDetailPage.tsx's
  // interviews.generate CARVE_OUT_NOTES entry for the user-facing summary
  // of what each of these two now covers.
  const canManageInterviewContent = can('interviews.generate')
  const canCreateInterview = can('interviews.create')
  // Added 2026-08-25 (permission-catalog round 2, §26) — Download JD had no
  // gate at all (purely client-side, no backend route). Default-open to
  // all roles, matching today's actual unrestricted behavior exactly.
  const canDownloadJD = can('jobs.downloadJD')
  // Fixed 2026-08-17 (shipped 2026-08-25): was `roleAtLeast(user?.role,
  // 'admin')` against the static 6-role ROLES array, which fails closed
  // (returns false) for any custom role — including a custom role holding
  // bypass-all, which should see this exactly like admin/super_admin do.
  // Reads live grants instead, mirroring `usePermission()`'s own
  // `bypassesOwnership` pattern. Same caveat as that pattern: a custom
  // role with admin-equivalent capability grants but no bypass-all flag
  // still won't match — no capability exists for this UI-only, backend-
  // unenforced display, so there's nothing to check it against.
  const isAdminDisplayOnly = isBypassAll || role === 'admin' || role === 'super_admin'

  // Can edit job-match assignments
  const canEditAssignments = can('assignments.manageJobMatches')
  // Can edit the job's own fields (Actions dropdown's "Edit" item) — bug
  // fix 2026-08-29: this was wired to canEditAssignments
  // (assignments.manageJobMatches, a different concern entirely — job-
  // match reassignment, not editing the job itself), found during an
  // audit of the Actions dropdown's per-item gating. JobsPage.tsx's list-
  // row Edit button already correctly used jobs.edit; this makes the
  // detail page's Edit action consistent with it.
  const canEditJob = can('jobs.edit')
  // Gates the Actions dropdown's trigger button itself, not just the items
  // inside it — bug fix 2026-08-30: the button rendered unconditionally
  // even for a role holding none of the four action capabilities below,
  // opening to a visually empty menu. OR of every item this dropdown can
  // contain (Edit/Download/Reprocess/Delete); hidden entirely when none
  // apply, same hide convention as the items themselves.
  const hasAnyJobAction = canEditJob || canDownloadJD || canReprocessJob || can('jobs.delete')
  // Can manage which recruiters are assigned to this job (Assigned Recruiters section)
  const canManageRecruiters = can('jobs.manageRecruiters')
  // Can change a candidate's status (Apply Matching tab)
  const canChangeCandidateStatus = can('candidateStatus.change')
  // Gate mismatch fix (Priority 4 audit) — the "Apply Resume"/"Bulk Apply"
  // triggers below rendered unconditionally even though the backend has
  // always gated this route at recruiter-tier (resume_upload_and_match.py).
  // Frontend-only change. Split off resumes.upload onto its own key,
  // jobs.applyResume, 2026-08-30 — this page's Apply Resume/Bulk Apply call
  // a materially different, job-scoped backend route than the Candidate
  // screen's general upload; same recruiter+ tier, no access change.
  const canApplyResume = can('jobs.applyResume')
  // Per-row "⋮" dropdown audit, 2026-08-30 — found ungated on this page:
  // "Reprocess comparison" reuses matching.newMatch (same real-AI-compute
  // concern as the standalone Apply Matching page's "Trigger a new AI
  // match run", not a new key); "View candidate profile" reuses
  // resumes.viewDetails, since it opens the exact same GET
  // /resumes/{id}/analysis endpoint that route already gates — the
  // trigger just never checked the capability the backend was already
  // enforcing, dead-ending into an error state instead of hiding.
  const canNewMatch = can('matching.newMatch')
  const canViewCandidateDetails = can('resumes.viewDetails')
  // "Download Resume" in this same per-row dropdown calls the identical
  // resumesApi.download() -> GET /resumes/{id}/download already gated
  // elsewhere by resumes.downloadOriginal — this page just never had its
  // own copy of the check.
  const canDownloadOriginal = can('resumes.downloadOriginal')
  // "Format with Client Template" — same capability as the plain formatted-
  // resume download elsewhere (resumes.downloadFormatted): no separate
  // resumeTemplates.* check, since using a template someone else published
  // is a consumption action, not template administration.
  const canDownloadFormatted = can('resumes.downloadFormatted')

  const [showActionsDropdown, setShowActionsDropdown] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [editingJob, setEditingJob] = useState<Job | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const actionsDropdownRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  // Refresh everything scoped to this job whenever the user switches tabs or
  // returns to the browser tab — an action taken in one tab (or on a
  // scorecard page) is then reflected in the others without a page reload.
  // Only queries currently on screen refetch; the rest are just marked stale.
  const refreshJobQueries = useCallback(() => {
    if (!jobId) return
    queryClient.invalidateQueries({
      // Interview config queries are skipped: their editors hold unsaved local
      // edits that a background refetch must never overwrite.
      predicate: (q) =>
        Array.isArray(q.queryKey)
        && q.queryKey.includes(jobId)
        && !String(q.queryKey[0]).includes('config'),
    })
    queryClient.invalidateQueries({ queryKey: ['stage-acknowledgements', jobId] })
  }, [jobId, queryClient])

  useEffect(() => {
    refreshJobQueries()
  }, [activeTab, refreshJobQueries])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshJobQueries()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refreshJobQueries])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        setShowActionsDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [actionsDropdownRef])

  // Close candidate actions dropdown on scroll (only main page scroll, not modal scroll)
  useEffect(() => {
    const handleScroll = (event: Event) => {
      // Only close dropdown if scrolling the main page, not inside modals
      const target = event.target
      if (showCandidateActionsDropdown && (target === document || target === document.documentElement || target === document.body)) {
        setShowCandidateActionsDropdown(null)
      }
    }
    
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [showCandidateActionsDropdown])

  // Reprocess job mutation (admin only)
  const reprocessJobMutation = useMutation({
    mutationFn: (id: string) => jobsApi.reprocess(id),
    onSuccess: async () => {
      toast.success('Job reprocessing started!')
      await queryClient.invalidateQueries({ queryKey: ['job', jobId] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Job reprocessing failed'
      toast.error(message)
    }
  })

  // Delete job mutation
  const deleteJobMutation = useMutation({
    mutationFn: (id:string) => jobsApi.delete(id),
    onSuccess: () => {
      toast.success('Job posting deleted successfully!')
      navigate('/jobs') // Navigate back to the jobs list
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to delete job'
      toast.error(message)
    }
  })

  // Update job mutation
  const updateJobMutation = useMutation({
    mutationFn: async ({ id, data, needsReprocessing }: { id: string, data: Partial<JobFormData>, needsReprocessing: boolean }) => {
      // First update the job
      const updateResponse = await jobsApi.update(id, data)

      // Only reprocess if content fields changed (description, responsibilities, requirements, benefits)
      if (needsReprocessing) {
        await jobsApi.reprocess(id)
      }

      // matches_invalidated (backend) is set when a matching-relevant field
      // (description/responsibilities/requirements/min-max experience)
      // changed — existing matches are stale but were never deleted, so
      // refresh them automatically instead of leaving it to a manual
      // "Apply Selected" click.
      return { matchesInvalidated: Boolean(updateResponse?.data?.matches_invalidated) }
    },
    onMutate: () => {
      // Close the modal immediately when update starts
      setEditingJob(null)
      setShowCreateModal(false)
    },
    onSuccess: async ({ matchesInvalidated }, { needsReprocessing }) => {
      console.log('🔄 UPDATE JOB SUCCESS', needsReprocessing ? '- Starting reprocessing...' : '- No reprocessing needed')

      if (needsReprocessing) {
        toast.success('Job posting updated successfully! Starting reprocessing...')

        // Wait a moment for backend processing
        await new Promise(resolve => setTimeout(resolve, 2000))
      } else {
        toast.success('Job posting updated successfully!')
      }

      // Refresh the job data
      await queryClient.invalidateQueries({ queryKey: ['job', jobId] })

      if (matchesInvalidated && jobId) {
        await autoRematchExistingCandidates(jobId)
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to update job'
      toast.error(message)
    }
  })

  // Re-runs matching for every candidate already applied to this job, without
  // requiring the recruiter to manually select them and click "Apply
  // Selected" — triggered automatically after a JD edit changes a
  // matching-relevant field (see updateJobMutation's matchesInvalidated).
  // Already-rejected candidates are excluded server-side, not here.
  const autoRematchExistingCandidates = async (jobIdToRematch: string) => {
    try {
      const existingResponse = await matchingApi.getComparisonResults(jobIdToRematch)
      const existingResumeIds: string[] = (existingResponse.data?.comparison_results || [])
        .map((r: any) => r.resume_id)
        .filter(Boolean)

      if (existingResumeIds.length === 0) {
        return
      }

      await matchingApi.compareCandidatesAsync([jobIdToRematch], existingResumeIds)
      toast.success(`Refreshing matches for ${existingResumeIds.length} candidate(s) against the updated job...`)

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['comparison-results', jobIdToRematch] }).catch(() => {})
      }, 2000)
    } catch (error) {
      console.error('Auto re-match after job update failed:', error)
      toast.error('Job updated, but automatically refreshing candidate matches failed. Please use "Apply Selected" manually.')
    }
  }

  // Update assignments mutation
  const updateAssignmentsMutation = useMutation({
    mutationFn: (userIds: string[]) => {
      if (!jobId) throw new Error('Job ID is required')
      
      // Validate: At least one recruiter must be assigned
      if (!userIds || userIds.length === 0) {
        throw new Error('At least one recruiter must be assigned to the job')
      }
      
      return jobsApi.updateJobRecruiters(jobId, userIds)
    },
    onSuccess: async () => {
      toast.success('Recruiters updated successfully!')
      setShowAssignmentModal(false)
      // Invalidate both the single job and the jobs list
      await queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      await queryClient.invalidateQueries({ queryKey: ['jobs'] })
    },
    onError: (error: any) => {
      const errorMessage = error.message || error.response?.data?.detail || 'Failed to update recruiters'
      toast.error(errorMessage)
    }
  })

  const downloadResumeMutation = useMutation({
    mutationFn: ({ resumeId }: { resumeId: string; candidateName: string; jobTitle: string }) => resumesApi.download(resumeId, jobId ?? undefined),
    onSuccess: async (response, { candidateName, jobTitle }) => {
      let extension = 'pdf'
      try {
        const ab = await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as ArrayBuffer)
          reader.onerror = reject
          reader.readAsArrayBuffer(response.data.slice(0, 4))
        })
        const bytes = new Uint8Array(ab)
        if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
          extension = 'pdf'
        } else if (bytes[0] === 0x50 && bytes[1] === 0x4B) {
          extension = 'docx'
        } else if (bytes[0] === 0xD0 && bytes[1] === 0xCF && bytes[2] === 0x11 && bytes[3] === 0xE0) {
          extension = 'doc'
        }
      } catch {
        extension = 'pdf'
      }

      const safeFilenamePart = (value: string, fallback: string) =>
        value.replace(/[<>:"/\\|?*\u0000-\u001F]/g, '').replace(/\s+/g, ' ').trim().replace(/[. ]+$/g, '') || fallback
      const filename = `${safeFilenamePart(jobTitle, 'Job')}_${safeFilenamePart(candidateName, 'Candidate')}.${extension}`
      const contentType = (response.headers['content-type'] as string | undefined) || 'application/octet-stream'
      const blob = new Blob([response.data], { type: contentType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success(`Resume downloaded: ${filename}`)
    },
    onError: (error: any) => {
      if (error.response?.status === 404) {
        toast.error('No resume file found for this candidate')
      } else {
        toast.error('Failed to download resume: ' + (error.response?.data?.detail || error.message))
      }
    }
  })

  // "Format with Client Template" — uses this job's own client automatically
  // (no picker, unlike the removed ResumesPage.tsx version): a candidate
  // being viewed here is already in the context of this specific job, so
  // there's a real client to format against, matching VMP's own placement
  // of this exact feature (always tied to the job/requirement being viewed,
  // never a free client pick).
  // Tracks exactly WHICH button(s) triggered an in-flight request (which
  // resume, plain vs. with-logo) - react-query's own mutation.isPending is
  // a single shared flag for the whole mutation object, so using it
  // directly on both "Formatted Resume" and "Formatted Resume with Logo"
  // made BOTH buttons show a spinner whenever EITHER one was clicked, for
  // EVERY candidate row on the page. A list (not a single object) so the
  // plain and with-logo variants for the same candidate can be in flight
  // at once - they're independent downloads, one running shouldn't block
  // or clear the other's own loading state.
  const [formatWithTemplateInFlight, setFormatWithTemplateInFlight] = useState<Array<{ resumeId: string; withLogo: boolean }>>([])
  const isFormatWithTemplateInFlight = (resumeId: string, withLogo: boolean) =>
    formatWithTemplateInFlight.some((f) => f.resumeId === resumeId && f.withLogo === withLogo)

  const formatWithTemplateMutation = useMutation({
    mutationFn: (vars: { resumeId: string; candidateName: string; withLogo: boolean }) =>
      formatResumeWithTemplate(vars.resumeId, job && job.client ? job.client : '', vars.withLogo),
    onMutate: (vars) => {
      setFormatWithTemplateInFlight((prev) => [...prev, { resumeId: vars.resumeId, withLogo: vars.withLogo }])
      toast.custom(
        (t) => (
          <div
            className={`flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-lg ${t.visible ? 'animate-enter' : 'animate-leave'}`}
          >
            <div className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <span className="text-sm text-gray-700">Download in progress…</span>
            <button
              type="button"
              onClick={() => toast.dismiss(t.id)}
              className="ml-2 flex-shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              title="Dismiss"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ),
        { id: 'format-with-template', duration: Infinity }
      )
    },
    onSuccess: (blob, vars) => {
      const candidateName = vars.candidateName || 'Professional'
      const clientName = job && job.client ? job.client : ''
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${candidateName}_${clientName}_Formatted_Resume${vars.withLogo ? '_WithLogo' : ''}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.dismiss('format-with-template')
      toast.success(`Formatted resume generated using ${clientName}'s template!`)
    },
    onError: (error: any) => {
      toast.dismiss('format-with-template')
      // TEMPLATE_INVALID (stale/unusable client template bindings) is
      // deliberately shown as a distinct, actionable message - never the
      // generic failure toast - and no download starts for it, same as
      // any other rejected mutation (onSuccess, where the file download
      // happens, never runs when the promise rejects).
      if (error.response?.data?.code === 'TEMPLATE_INVALID') {
        toast.error(
          error.response.data.message ||
            "This client's resume template needs to be re-bound before it can be used.",
          { duration: 8000 }
        )
        return
      }
      toast.error(error.response?.data?.detail || 'Failed to format resume with client template')
    },
    onSettled: (_data, _error, vars) => {
      setFormatWithTemplateInFlight((prev) =>
        prev.filter((f) => !(f.resumeId === vars.resumeId && f.withLogo === vars.withLogo))
      )
    },
  })

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    if (jobId) {
      deleteJobMutation.mutate(jobId)
    }
    setIsDeleteModalOpen(false)
  }

  // Download job as Word document
  const handleDownloadJob = async () => {
    if (!job) return
    
    try {
      await downloadJobAsWord({
        title: job.title,
        department: job.department,
        location: job.location,
        employment_type: job.employment_type,
        remote_option: job.remote_option,
        description: job.description,
        responsibilities: job.responsibilities,
        requirements: job.requirements,
        benefits: job.benefits,
        salary_min: job.salary_min,
        salary_max: job.salary_max,
        salary_currency: job.salary_currency
      })
      toast.success('Job description downloaded successfully!')
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Failed to download job description')
    }
  }

  // Helper function to shorten Job ID
  const shortenJobId = (jobId: string): string => {
    if (!jobId) return ''
    return jobId.substring(0, 8)
  }

  // Helper function to render text with inline formatting
  const renderFormattedText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g)
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.replace(/\*\*/g, '')
        return <strong key={index} className="font-semibold text-gray-900">{boldText}</strong>
      } else if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
        const italicText = part.replace(/\*/g, '')
        return <em key={index} className="italic text-gray-600">{italicText}</em>
      } else {
        return <span key={index}>{part}</span>
      }
    })
  }

  // Render structured skills analysis data
  const renderStructuredSkillsAnalysis = (content: string) => {
    try {
      const data = JSON.parse(content)
      const elements: React.ReactElement[] = []
      let currentIndex = 0

      Object.entries(data).forEach(([categoryKey, categoryData]: [string, any]) => {
        if (categoryData && typeof categoryData === 'object' && categoryData.title && categoryData.items) {
          elements.push(
            <CollapsibleCard
              key={currentIndex++}
              title={categoryData.title}
              subtitle={categoryData.description}
              icon={<CpuChipIcon className="w-4 h-4 text-blue-600" />}
              defaultOpen
              compact
            >
              <div className="space-y-2">
                {categoryData.items.map((item: any, itemIndex: number) => (
                  <div key={itemIndex} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <h6 className="text-sm font-semibold text-gray-900 mb-1">
                      {item.skill || item.tool || item.competency || 'Item'}
                    </h6>
                    <p className="text-sm text-gray-700 mb-1 leading-relaxed">
                      {item.description}
                    </p>
                    {item.importance && (
                      <p className="text-xs text-gray-600 italic">
                        <span className="font-medium">Importance:</span> {item.importance}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleCard>
          )
        }
      })

      if (data.supporting_evidence && Array.isArray(data.supporting_evidence)) {
        elements.push(
          <CollapsibleCard
            key={currentIndex++}
            title="Supporting Evidence"
            icon={<CheckCircleIcon className="w-4 h-4 text-green-600" />}
            defaultOpen
            compact
          >
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <ul className="space-y-2">
                {data.supporting_evidence.map((evidence: string, index: number) => (
                  <li key={index} className="flex items-start text-sm text-green-800">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="leading-relaxed">{evidence}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CollapsibleCard>
        )
      }

      if (data.suggestions && Array.isArray(data.suggestions)) {
        elements.push(
          <CollapsibleCard
            key={currentIndex++}
            title="Suggestions"
            icon={<LightBulbIcon className="w-4 h-4 text-yellow-600" />}
            defaultOpen
            compact
          >
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <ul className="space-y-2">
                {data.suggestions.map((suggestion: string, index: number) => (
                  <li key={index} className="flex items-start text-sm text-yellow-800">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="leading-relaxed">{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CollapsibleCard>
        )
      }

      return <div className="space-y-3">{elements}</div>
    } catch (error) {
      console.error('Error parsing structured skills analysis:', error)
      return formatResponseContent(content)
    }
  }

  // Render structured experience assessment data
  const renderStructuredExperienceAssessment = (content: string) => {
    try {
      const data = JSON.parse(content)
      const elements: React.ReactElement[] = []
      let currentIndex = 0

      Object.entries(data).forEach(([categoryKey, categoryData]: [string, any]) => {
        if (categoryData && typeof categoryData === 'object' && categoryData.title && categoryData.items) {
          elements.push(
            <div key={currentIndex++} className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <ChartBarIcon className="w-5 h-5 text-purple-600 mr-2" />
                {categoryData.title}
              </h4>
              {categoryData.description && (
                <p className="text-sm text-gray-600 mb-4">{categoryData.description}</p>
              )}
              <div className="space-y-3">
                {categoryData.items.map((item: any, itemIndex: number) => (
                  <div key={itemIndex} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h6 className="text-sm font-semibold text-gray-900 mb-2">
                      {item.experience || item.industry || item.leadership || item.project || 'Item'}
                    </h6>
                    <p className="text-sm text-gray-700 mb-2 leading-relaxed">
                      {item.description}
                    </p>
                    {item.importance && (
                      <p className="text-xs text-gray-600 italic">
                        <span className="font-medium">Importance:</span> {item.importance}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        }
      })

      if (data.supporting_evidence && Array.isArray(data.supporting_evidence)) {
        elements.push(
          <div key={currentIndex++} className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
              Supporting Evidence
            </h4>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <ul className="space-y-2">
                {data.supporting_evidence.map((evidence: string, index: number) => (
                  <li key={index} className="flex items-start text-sm text-green-800">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="leading-relaxed">{evidence}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      }

      if (data.suggestions && Array.isArray(data.suggestions)) {
        elements.push(
          <div key={currentIndex++} className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <LightBulbIcon className="w-5 h-5 text-yellow-600 mr-2" />
              Suggestions
            </h4>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <ul className="space-y-2">
                {data.suggestions.map((suggestion: string, index: number) => (
                  <li key={index} className="flex items-start text-sm text-yellow-800">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="leading-relaxed">{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      }

      return <div className="space-y-4">{elements}</div>
    } catch (error) {
      console.error('Error parsing structured experience:', error)
      return formatResponseContent(content)
    }
  }

  // Format response content for better readability
  const formatResponseContent = (content: string) => {
    if (!content) return null
    
    const lines = content.split('\n')
    const elements: React.ReactElement[] = []
    let currentIndex = 0
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      
      if (!line) continue
      
      if (line.startsWith('###')) {
        const title = line.replace(/^###\s*/, '').trim()
        elements.push(
          <h3 key={currentIndex++} className="text-xl font-bold text-gray-900 mb-4 mt-6 flex items-center">
            <CpuChipIcon className="w-6 h-6 text-blue-600 mr-3" />
            {title}
          </h3>
        )
      }
      else if (line.startsWith('##')) {
        const title = line.replace(/^##\s*/, '').trim()
        elements.push(
          <h4 key={currentIndex++} className="text-lg font-semibold text-gray-900 mb-3 mt-5 flex items-center">
            <ChartBarIcon className="w-5 h-5 text-purple-600 mr-2" />
            {title}
          </h4>
        )
      }
      else if (line.match(/^\*\*.*\*\*$/)) {
        const title = line.replace(/\*\*/g, '').trim()
        elements.push(
          <h5 key={currentIndex++} className="text-md font-semibold text-gray-800 mb-2 mt-4">
            {title}
          </h5>
        )
      }
      else if (line.includes(':') && !line.startsWith('-') && !line.startsWith('•') && !line.match(/^\d+\./)) {
        const skillName = line.split(':')[0].trim()
        const description = line.split(':').slice(1).join(':').trim()
        
        let importance = ''
        if (i + 1 < lines.length && lines[i + 1].trim().startsWith('*Importance*:')) {
          importance = lines[i + 1].trim().replace('*Importance*:', '').trim()
          i++
        }
        
        elements.push(
          <div key={currentIndex++} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h6 className="text-sm font-semibold text-gray-900 mb-2">
              {renderFormattedText(skillName)}
            </h6>
            <p className="text-sm text-gray-700 mb-2 leading-relaxed">
              {renderFormattedText(description)}
            </p>
            {importance && (
              <p className="text-xs text-gray-600 italic">
                <span className="font-medium">Importance:</span> {renderFormattedText(importance)}
              </p>
            )}
          </div>
        )
      }
      else if (line.match(/^[-•]\s/)) {
        const text = line.replace(/^[-•]\s*/, '').trim()
        elements.push(
          <div key={currentIndex++} className="flex items-start mb-2 ml-4">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <span className="text-sm text-gray-700 leading-relaxed">
              {renderFormattedText(text)}
            </span>
          </div>
        )
      }
      else if (line.match(/^\d+\.\s/)) {
        const text = line.replace(/^\d+\.\s*/, '').trim()
        elements.push(
          <div key={currentIndex++} className="flex items-start mb-2 ml-4">
            <span className="text-sm font-medium text-blue-600 mr-2 mt-0.5">{line.match(/^\d+\./)?.[0]}</span>
            <span className="text-sm text-gray-700 leading-relaxed">
              {renderFormattedText(text)}
            </span>
          </div>
        )
      }
      else if (line.length > 0) {
        elements.push(
          <p key={currentIndex++} className="text-sm text-gray-700 mb-3 leading-relaxed">
            {renderFormattedText(line)}
          </p>
        )
      }
    }
    
    return <div className="space-y-2">{elements}</div>
  }

  

  // Fetch job details
  const { data: job, isLoading, error } = useQuery<Job>({
    queryKey: ['job', jobId],
    queryFn: async () => {
      const response = await jobsApi.getById(jobId!)
      return response.data
    },
    enabled: !!jobId,
    retry: 0,
  })

  // Tab configuration. isTalentosJob mirrors ResumeDetailPage.tsx's
  // isTalentosProfile exactly -- "Job Overview" replaces Overview/Required
  // Skills/Experience when TalentOS has analysed this job; Apply Matching
  // and every Interview tab are unaffected either way.
  const isTalentosJob = SHOW_TALENTOS_JOB_UI && job?.analysis_engine === 'talentos'
  const allJobTabs = isTalentosJob
    ? [
        { id: 'overview', name: 'Job Overview', icon: EyeIcon },
        { id: 'matching', name: 'Apply Matching', icon: UserGroupIcon },
        // Straight after Apply Matching: a voice screening is the first
        // thing that happens to a candidate once they are in the pipeline,
        // before any interview round. Unaffected by TalentOS vs. Foundry.
        // { id: 'voiceScreening', name: 'Voice Screening', icon: PhoneIcon },
        { id: 'interview', name: 'Interview', icon: MicrophoneIcon },
        { id: 'proctoredInterview', name: 'Proctored Assessment', icon: ShieldCheckIcon },
        { id: 'liveInterview', name: 'Live Interview', icon: VideoCameraIcon }
      ]
    : [
        { id: 'overview', name: 'Overview', icon: EyeIcon },
        { id: 'skills', name: 'Required Skills', icon: CpuChipIcon },
        { id: 'experience', name: 'Experience', icon: ChartBarIcon },
        { id: 'matching', name: 'Apply Matching', icon: UserGroupIcon },
        // { id: 'voiceScreening', name: 'Voice Screening', icon: PhoneIcon },
        { id: 'interview', name: 'Interview', icon: MicrophoneIcon }, // hidden per request — tab + content commented out below
        { id: 'proctoredInterview', name: 'Proctored Assessment', icon: ShieldCheckIcon },
        { id: 'liveInterview', name: 'Live Interview', icon: VideoCameraIcon }
      ]

  // Filter Interview tab for senior_recruiter and up. Converted 2026-08-14
  // (§10.1) to an OR across the interview-tier capabilities rather than a
  // single proxy — same reasoning as ResumeDetailPage's equivalent check:
  // this is whole-tab-container visibility, never tied to one action, so a
  // single proxy would only be correct by coincidence.
  //
  // Interview, Proctored Interview, and Live Interview are 3 fully
  // independent features as of 2026-10-07 — each gated on its own
  // capability keys, no shared/borrowed gate between them. Proctored
  // Interview previously proxied its visibility off the AI Interview keys
  // below (`interviews.*`) because `proctoredInterviews.manage`/`.view`
  // didn't exist in the RBAC catalog yet; they're now seeded (own
  // "Proctored Interview" module in create_rbac_defaults.py's PERMISSIONS
  // list) and the backend's `gated(...)` calls in proctored_interviews.py
  // already expected these exact strings, so this is a pure de-link, not a
  // new backend dependency.
  //
  // As with any newly-seeded key, the seed script still needs to be RUN
  // against each environment (dev/staging/prod) before role_permission
  // grants actually exist there — a deliberate, explicit deployment step,
  // not something that happens automatically on merge. Until it's run in a
  // given environment, this tab stays visible there only to an
  // `is_bypass_all` role.
  //
  // Proctored Interview and Live Interview changed 2026-10-08 (explicit
  // user decision) from an OR across view/manage to View-exclusive: a role
  // holding only *.manage (no *.view) now has this tab hidden entirely,
  // even though it could otherwise create/edit sessions — by design, "can't
  // view it" means "can't see it at all" for these two features. It's on
  // whoever edits a role to grant View alongside Manage; the system no
  // longer infers one from the other for tab visibility. (AI Interview's
  // own tab above is deliberately NOT changed — it was never part of this
  // request, and its visibility is already a 4-way OR across more granular
  // keys, not a simple view/manage pair.) See the render guards below
  // (`activeTab === 'proctoredInterview' && can(...)`) for the matching
  // fix to direct ?tab= URL navigation, which bypasses this filter
  // entirely on its own.
  const tabs = allJobTabs.filter(tab => {
    if (tab.id === 'interview') {
      return can('interviews.view') || can('interviews.create') || can('interviews.generate') || can('interviewGuides.generate')
    }
    if (tab.id === 'proctoredInterview') {
      return can('proctoredInterviews.view')
    }
    if (tab.id === 'liveInterview') {
      return can('liveInterview.view')
    }
    if (tab.id === 'voiceScreening') {
      // The same capability the Apply Matching page is routed behind. This
      // tab lists candidates' phone numbers and can place a real call, so
      // "may work this job's candidates" is the right bar — the actions
      // inside are separately enforced server-side.
      return can('matching.view')
    }
    return true
  })

  // Fetch resumes for matching
  const { data: resumesData, isLoading: resumesLoading } = useQuery({
    queryKey: ['resumes-for-matching', searchQuery],
    queryFn: () => matchingApi.searchResumes({ query: searchQuery, page_size: 100 }),
    enabled: activeTab === 'matching' && !!searchQuery,
    staleTime: 60000,
    refetchOnMount: 'always',
  })

  // Fetch comparison results — always refetch on mount so navigating to a job shows fresh data.
  //
  // Polling: uses refetchInterval (time-based, driven by React Query's own
  // fetch-completion events) rather than a useEffect keyed on `comparisonData`.
  // React Query applies structural sharing — a refetch that returns data
  // identical to what's cached is given back the SAME object reference, so a
  // useEffect gated on that reference never re-fires while a candidate sits at
  // "processing" (exactly the state we need to keep polling through). refetchInterval
  // doesn't have this problem: React Query re-evaluates it after every fetch
  // attempt regardless of whether `data` changed.
  //
  // Negative-scenario handling:
  // - query.state.status === 'error': React Query has already exhausted its own
  //   retries for this fetch; stop polling rather than hammering a failing endpoint.
  // - dataUpdateCount cap: bounds the loop even if a result is legitimately stuck
  //   at pending/processing forever (stuck task, crashed worker) — see constants above.
  // - malformed/missing body: treated as inactive (no poll), not an error.
  const {
    data: comparisonData,
    isLoading: comparisonDataLoading,
    dataUpdatedAt: comparisonDataUpdatedAt,
  } = useQuery({
    queryKey: ['comparison-results', jobId],
    queryFn: () => matchingApi.getComparisonResults(jobId!),
    enabled: activeTab === 'matching' && !!jobId,
    refetchOnMount: 'always',
    staleTime: 0,
    refetchInterval: (query: any) => {
      if (query.state.status === 'error') return false
      if (query.state.dataUpdateCount > MATCH_POLL_MAX_ATTEMPTS) return false
      const body = query.state.data?.data
      if (!body) return false
      const isActive =
        body.status === 'queued' ||
        body.status === 'processing' ||
        (body.comparison_results || []).some(
          (r: any) => r.status === 'pending' || r.status === 'processing'
        )
      return isActive ? MATCH_POLL_INTERVAL_MS : false
    },
  })

  // True only for the first fetch on this job (comparisonResults is still
  // null) — avoids flashing "No comparison data"/"No results yet" empty
  // states for the moment before the initial response lands. Background
  // refetches/polls (comparisonResults already populated) don't trigger it.
  const isInitialMatchLoading = activeTab === 'matching' && comparisonDataLoading && !comparisonResults

  // ── TalentOS ──────────────────────────────────────────────────────────────
  // The newest TalentOS run per candidate on this job, in one round-trip.
  // Read-only: it never triggers scoring, so mounting it here is free.
  // Keyed by resume_id — see TalentosScoreCell for why the card score and the
  // report have to come from the same run.
  const talentosMatches = useTalentosMatchesForJob(
    activeTab === 'matching' ? jobId : undefined,
  )

  // Fetch resume data for popup
  const { data: resumeData, isLoading: resumeLoading, error: resumeError } = useQuery({
    queryKey: ['resume-detail', selectedResumeId],
    queryFn: () => resumesApi.getById(selectedResumeId!),
    enabled: !!selectedResumeId,
    retry: 1
  })


  // Load comparison results when switching to matching tab — also re-syncs on
  // every poll tick (via comparisonDataUpdatedAt), not just when `comparisonData`
  // gets a new object reference. React Query's structural sharing can hand back
  // the same reference for a content-identical refetch, which would otherwise
  // starve this effect exactly while a candidate is stuck mid-poll.
  useEffect(() => {
    if (activeTab === 'matching' && jobId && comparisonData) {
      if (comparisonData.data.comparison_results && comparisonData.data.comparison_results.length > 0) {
        setComparisonResults(comparisonData.data)
        setShowComparison(true)

        // Load candidate statuses (candidate_status_id) for all candidates
        const statuses: Record<string, string> = {}
        for (const result of comparisonData.data.comparison_results) {
          // Use the candidate_status_id from the result if set, otherwise leave unset
          statuses[result.resume_id] = result.candidate_status_id || ''
        }
        setApplicationStatuses(statuses)

        // Keep the native TalentOS badge/modal in step with this same poll
        // cycle. ensure_and_match (backend) always inserts a fresh
        // talentos_match_runs row BEFORE this job_match row flips to
        // completed, in the same task — so by the time comparison-results
        // shows fresh data here, TalentOS's own tables already have it too.
        // Their query hooks just have a 5-minute staleTime and no polling of
        // their own, so without this they'd sit on a stale cached run until
        // that expires or the page reloads. Bounded by the same lifecycle as
        // the poll above: this only runs while something here is actually
        // pending/processing, and stops the moment that stops.
        queryClient.invalidateQueries({ queryKey: ['talentos', 'matches-by-job', jobId] })
        for (const result of comparisonData.data.comparison_results) {
          queryClient.invalidateQueries({ queryKey: ['talentos', 'match', jobId, result.resume_id] })
        }
      }
    }
  }, [activeTab, jobId, comparisonData, comparisonDataUpdatedAt, queryClient])

  const resumes = resumesData?.data?.resumes || []

  // Get list of resume IDs already in analysis results
  const getAlreadyAnalyzedCandidates = () => {
    if (!comparisonResults?.comparison_results) return []
    return comparisonResults.comparison_results.map((result: any) => result.resume_id)
  }

  const alreadyAnalyzedCandidates = getAlreadyAnalyzedCandidates()

  // Matching handler functions
  const handleCandidateSelect = (candidateId: string) => {
    // Do not allow selection if candidate is already analyzed
    if (alreadyAnalyzedCandidates.includes(candidateId)) {
      return
    }
    
    // Do not allow selection if candidate is blacklisted
    const candidate = resumes.find((r: Resume) => r.id === candidateId)
    if (candidate?.blacklist) {
      return
    }
    
    setSelectedCandidates(prev => 
      prev.includes(candidateId) 
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    )
  }

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setSearchQuery(searchTerm.trim())
    } else {
      setSearchQuery('')
    }
  }

  const handleClearSearch = () => {
    setSearchTerm('')
    setSearchQuery('')
    setSelectedCandidates([])
  }

  const handleCompareCandidates = async () => {
    if (!jobId || selectedCandidates.length === 0) {
      toast.error('Please select at least one candidate to compare')
      return
    }

    try {
      setComparisonLoading(true)
      
      // Start async comparison
      const response = await matchingApi.compareCandidatesAsync([jobId], selectedCandidates)
      const { task_id, message } = response.data
      
      // Load existing results first, then show queued status
      try {
        const existingResponse = await matchingApi.getComparisonResults(jobId)
        if (existingResponse.data.comparison_results && existingResponse.data.comparison_results.length > 0) {
          // Keep existing results and add queued status
          setComparisonResults({
            ...existingResponse.data,
            task_id,
            status: 'queued',
            message,
            progress: 0
          })
        } else {
          // No existing results, show empty state with queued status
          setComparisonResults({
            task_id,
            status: 'queued',
            message,
            progress: 0,
            comparison_results: [],
            summary_insights: {
              total_candidates: selectedCandidates.length,
              average_score: 0,
              top_candidate: null,
              score_distribution: { excellent: 0, good: 0, fair: 0, poor: 0 }
            }
          })
        }
      } catch (error) {
        // If no existing results, show empty state
        setComparisonResults({
          task_id,
          status: 'queued',
          message,
          progress: 0,
          comparison_results: [],
          summary_insights: {
            total_candidates: selectedCandidates.length,
            average_score: 0,
            top_candidate: null,
            score_distribution: { excellent: 0, good: 0, fair: 0, poor: 0 }
          }
        })
      }
      setShowComparison(true)
      
      // Clear search results and selections
      setSearchTerm('')
      setSearchQuery('')
      setSelectedCandidates([])
      
      // Hand off to React Query after the optimistic "queued" state above has had
      // a moment on screen — invalidating (not a one-off fetch) means the
      // refetchInterval poll takes over from here and keeps checking until the
      // new comparisons actually finish, rather than freezing on one snapshot.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['comparison-results', jobId] }).catch(() => {})
      }, 2000)

    } catch (error) {
      console.error('Comparison failed:', error)
      toast.error('Failed to start comparison. Please try again.')
      setComparisonLoading(false)
    }
  }



  const handleReprocessRecord = async (resumeId: string) => {
    if (!jobId) return

    try {
      // Add to reprocessing set — drives the dropdown button's own spinner only,
      // unrelated to the card's status badge (that one reads live query data).
      setReprocessingRecords(prev => new Set([...prev, resumeId]))

      // Call reprocess API
      await matchingApi.reprocessComparisonRecord(jobId, resumeId)

      // Give the backend a moment to flip the row to pending/processing, then
      // invalidate the query cache — NOT a one-off manual fetch. Routing through
      // React Query here (rather than a direct fetch + setComparisonResults)
      // means the refetchInterval poll above picks this candidate back up
      // automatically and keeps checking until it actually finishes, instead of
      // freezing on whatever this single snapshot happened to show.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['comparison-results', jobId] }).catch(() => {})
        setReprocessingRecords(prev => {
          const newSet = new Set(prev)
          newSet.delete(resumeId)
          return newSet
        })
      }, 2000) // Wait 2 seconds for processing to start

      toast.success('Reprocessing started for selected candidate')
    } catch (error) {
      console.error('Failed to reprocess record:', error)
      toast.error('Failed to reprocess record. Please try again.')
      // Remove from reprocessing set on error
      setReprocessingRecords(prev => {
        const newSet = new Set(prev)
        newSet.delete(resumeId)
        return newSet
      })
    }
  }

  // "Retry" on a row whose Analysis Failed (JobMatch.status === 'failed') --
  // unlike handleReprocessRecord above (matching-only, silently no-ops when
  // the résumé itself never finished analysis), this re-runs analysis on
  // this SAME résumé record via resumes.reprocess(job_id=...) and lets the
  // backend re-trigger matching once it succeeds. Deliberately not a
  // re-upload: that goes through duplicate detection and creates a second
  // résumé record for the same person. Shares reprocessingRecords with
  // handleReprocessRecord (same button-spinner state, mutually exclusive —
  // a row is never both at once).
  const handleRetryAnalysis = async (resumeId: string) => {
    if (!jobId) return

    try {
      setReprocessingRecords(prev => new Set([...prev, resumeId]))
      await resumesApi.reprocess(resumeId)

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['comparison-results', jobId] }).catch(() => {})
        setReprocessingRecords(prev => {
          const newSet = new Set(prev)
          newSet.delete(resumeId)
          return newSet
        })
      }, 2000)

      toast.success('Retrying analysis for this candidate')
    } catch (error) {
      console.error('Failed to retry analysis:', error)
      toast.error('Failed to retry analysis. Please try again.')
      setReprocessingRecords(prev => {
        const newSet = new Set(prev)
        newSet.delete(resumeId)
        return newSet
      })
    }
  }

  // Candidate status handler (newStatus is a candidate_status_id; comment is required)
  const handleApplicationStatusChange = async (resumeId: string, newStatus: string, comment: string) => {
    if (!jobId) return

    try {
      // Optimistic update
      setApplicationStatuses(prev => ({ ...prev, [resumeId]: newStatus }))

      // Call API to update status
      await matchingApi.updateApplicationStatus(jobId, resumeId, newStatus, comment)

      // Update comparisonResults state to persist the change. Also refresh the
      // status metadata (name/code/stage) from the master list so the Status
      // filter rebuilds correctly — updating only the id leaves a stale
      // status_name and the filter shows a duplicate old label until refresh.
      const meta = candidateStatusById.get(newStatus)
      setComparisonResults((prev: any) => {
        if (!prev?.comparison_results) return prev
        return {
          ...prev,
          comparison_results: prev.comparison_results.map((result: any) =>
            result.resume_id === resumeId
              ? {
                  ...result,
                  candidate_status_id: newStatus,
                  status_code: meta?.statusCode ?? result.status_code,
                  status_name: meta?.statusName ?? result.status_name,
                  stage_id: meta?.stageId ?? result.stage_id,
                  stage_name: meta?.stageName ?? result.stage_name,
                }
              : result
          )
        }
      })

      // Invalidate audit history to show the new status change
      queryClient.invalidateQueries({ queryKey: ['audit-history'] })

      console.log(`Successfully updated candidate status for ${resumeId} to ${newStatus}`)
    } catch (error) {
      console.error('Failed to update candidate status:', error)
      // Revert optimistic update on error
      setApplicationStatuses(prev => {
        const oldStatus = comparisonResults?.comparison_results?.find(
          (r: any) => r.resume_id === resumeId
        )?.candidate_status_id || ''
        return { ...prev, [resumeId]: oldStatus }
      })
      // Rethrow rather than swallow: every caller (ApplicationStatusDropdown's
      // own applyStatus, and TalentosMatchReport's Approve/Deny modal) awaits
      // this function inside its own try/catch specifically to show a success
      // toast only when the update actually succeeded and an error toast
      // otherwise. Swallowing the error here made both callers show "success"
      // even on failure -- the error toast belongs to the caller, not here,
      // so it isn't shown twice.
      throw error
    }
  }

  // Interview handler functions
  const handleGenerateInterview = async (resumeId: string, level: 'L1' | 'L2' = 'L2') => {
    if (!jobId || !job) return

    try {
      // Set specific loading state based on level
      if (level === 'L1') {
        setL1Loading(resumeId)
      } else {
        setL2Loading(resumeId)
      }
      setSelectedCandidateForInterview(resumeId)

      if (level === 'L1') {
        // L1: Use the job's existing interview questions
        // This check now ensures the questions are a valid string before proceeding
        if (job.interview_questions && typeof job.interview_questions === 'string') {
          setInterviewGuide({ questions: job.interview_questions, isJobLevel: true })
          setShowInterviewModal(true)
          //toast.success('Loaded general interview questions!')
        } else {
          toast.error('No general interview questions found for this job.')
        }
      } else {
        // L2: Generate customized questions based on resume + JD
        const response = await interviewApi.generateInterview({
          resume_id: resumeId,
          job_id: jobId,
          interview_level: 'L2'
        })
        
        setInterviewGuide(response.guide)
        setShowInterviewModal(true)
        
        if (response.is_new_generation) {
          toast.success('Custom interview questions generated successfully!')
        }
      }
    } catch (error) {
      console.error('Failed to process interview questions:', error)
      toast.error('Failed to process interview questions. Please try again.')
    } finally {
      // Clear specific loading state
      if (level === 'L1') {
        setL1Loading(null)
      } else {
        setL2Loading(null)
      }
    }
  }

  const handleReprocessInterview = async () => {
    if (!jobId || !selectedCandidateForInterview) return
    
    try {
      setInterviewReprocessing(true)
      
      const response = await interviewApi.reprocessInterview({
        resume_id: selectedCandidateForInterview,
        job_id: jobId,
        interview_level: 'L2' // Default to L3, could be made configurable
      })
      
      setInterviewGuide(response.guide)
      toast.success('Interview questions regenerated successfully!')
    } catch (error) {
      console.error('Failed to reprocess interview questions:', error)
      toast.error('Failed to regenerate interview questions. Please try again.')
    } finally {
      setInterviewReprocessing(false)
    }
  }

  const handleCloseInterviewModal = () => {
    setShowInterviewModal(false)
    setInterviewGuide(null)
    setSelectedCandidateForInterview(null)
  }

  // Handler for viewing candidate profile
  const handleViewCandidateProfile = (resumeId: string) => {
    setSelectedResumeId(resumeId)
    setShowResumePopup(true)
  }

  // Assignment tracking handlers
  const handleEditAssignment = (
    entityType: 'resume_upload' | 'job_match',
    entityId: string,
    entityTitle: string,
    currentUserId: string | null,
    currentUserName: string | null
  ) => {
    setSelectedAssignment({
      entityType,
      entityId,
      entityTitle,
      currentUserId,
      currentUserName,
    })
    setShowAssignmentEdit(true)
  }

  const handleViewAuditTrail = (
    entityType: 'resume_upload' | 'job_match',
    entityId: string,
    entityTitle: string
  ) => {
    setSelectedAssignment({
      entityType,
      entityId,
      entityTitle,
      currentUserId: null,
      currentUserName: null,
    })
    setShowAuditTrail(true)
  }

  const handleAssignmentSaved = () => {
    // Refresh comparison results data
    queryClient.invalidateQueries({ queryKey: ['comparison-results', jobId] })
    toast.success('Assignment updated successfully')
  }

  // Handler for uploading resume directly in job detail page
  // Proceed with actual upload (after duplicate check or user confirmation)
  const proceedWithUploadAndMatch = async (fileToUpload?: File) => {
    const file = fileToUpload || pendingFile
    if (!file) return
    
    // Validate source is selected
    if (!resumeSourceId) {
      toast.error('Please select a source before uploading')
      return
    }
    
    try {
      setUploadingResume(true)
      
      // Create form data
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source_id', resumeSourceId)

      // Use upload-and-match endpoint (async version)
      toast.loading('Uploading resume...', { id: 'upload-and-match' })
      console.log('Starting upload-and-match for job:', jobId)
      
      const response = await resumesApi.uploadAndMatch(jobId!, formData)
      console.log('Upload-and-match response:', response)
      
      const candidateName = response.data.candidate_name || 'Candidate'
      const resumeId = response.data.resume_id
      
      // Show duplicate warning if detected during upload
      if (response.data.is_duplicate && response.data.duplicate_confidence && response.data.duplicate_confidence >= 0.6) {
        toast(
          `⚠️ Possible duplicate detected (${(response.data.duplicate_confidence * 100).toFixed(0)}% confidence)`,
          { 
            duration: 5000,
            icon: '⚠️',
            style: {
              background: '#fef3c7',
              color: '#92400e',
              border: '1px solid #fbbf24'
            }
          }
        )
      }
      
      toast.success(`${candidateName} resume uploaded. Analyzing...`, {
        id: 'upload-and-match',
        duration: 4000
      })

      // Reset upload UI but keep the section visible to show the analyzing indicator
      setUploadingResume(false)
      setPendingFile(null)
      setDuplicateData(null)
      setShowDuplicateModal(false)
      if (fileInputRef.current) fileInputRef.current.value = ''

      // Show "Analyzing resume..." indicator inside the upload section
      setUploadMatchState({ stage: 'analyzing', candidateName })

      // Poll resume status every 50s; once analyzed → hide section + reload list once
      const MAX_POLLS = 24 // 20 minutes max
      let pollCount = 0
      const pollInterval = setInterval(async () => {
        pollCount++
        try {
          const statusResponse = await resumesApi.getUploadAndMatchStatus(resumeId, jobId!)
          const { resume_status, processing_error_display } = statusResponse.data

          if (resume_status === 'analyzed') {
            clearInterval(pollInterval)
            setShowUploadSection(false)
            setUploadMatchState(null)
            await queryClient.invalidateQueries({ queryKey: ['comparison-results', jobId] })
            toast.success(`${candidateName} resume processed. Results updated.`, { duration: 4000 })
          } else if (resume_status === 'failed') {
            clearInterval(pollInterval)
            setShowUploadSection(false)
            setUploadMatchState(null)
            toast.error(`Resume processing failed for ${candidateName}: ${processing_error_display || 'Failed — please try again'}`, { duration: 5000 })
          } else if (pollCount >= MAX_POLLS) {
            clearInterval(pollInterval)
            setShowUploadSection(false)
            setUploadMatchState(null)
            console.warn('Resume processing poll timed out')
            // Previously this closed the panel with no explanation, so the upload
            // simply vanished. Tell the user it is still running in the background.
            toast(
              `Still processing ${candidateName}'s resume. It will keep running in the background — refresh in a few minutes to see the result.`,
              { duration: 6000, icon: '⏳' }
            )
            await queryClient.invalidateQueries({ queryKey: ['comparison-results', jobId] })
          }
        } catch (err) {
          console.warn('Resume status poll error:', err)
          if (pollCount >= MAX_POLLS) {
            clearInterval(pollInterval)
            setShowUploadSection(false)
            setUploadMatchState(null)
          }
        }
      }, 50000)
      
    } catch (error: any) {
      console.error('Upload and match failed:', error)
      const errorMessage = error.response?.data?.detail || 'Failed to upload and match resume'
      toast.error(errorMessage, { id: 'upload-and-match' })
      setUploadingResume(false)
      setPendingFile(null)
    }
  }

  // Handler for uploading resume directly in job detail page
  const handleUploadResume = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate source is selected FIRST
    if (!resumeSourceId) {
      toast.error('Please select a source before uploading')
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    // Validate file type. Check the extension as well as the MIME type: browsers
    // report .doc as an empty string or application/octet-stream depending on the
    // OS, so a MIME-only check rejected valid Word documents.
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    const extension = file.name.split('.').pop()?.toLowerCase() || ''
    const allowedExtensions = ['pdf', 'doc', 'docx']
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension)) {
      toast.error('Please upload a PDF or Word document')
      // Reset the input, otherwise re-picking the same file fires no change
      // event and the upload box appears dead.
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    try {
      setUploadingResume(true)
      setPendingFile(file)
      
      // Create form data for duplicate check
      const formData = new FormData()
      formData.append('file', file)
      
      // First check for duplicates
      toast.loading('Checking for duplicates...', { id: 'duplicate-check' })
      const duplicateResponse = await resumesApi.searchDuplicates(formData)
      toast.dismiss('duplicate-check')
      
      console.log('🔍 Duplicate check response:', duplicateResponse)
      const data = duplicateResponse.data
      
      if (data.total_found > 0) {
        // Show duplicate detection modal
        setDuplicateData({
          has_duplicates: true,
          extracted_info: {
            candidate_name: data.search_criteria.name || null,
            candidate_email: data.search_criteria.email || null,
            candidate_phone: data.search_criteria.phone || null,
            file_name: file.name,
            file_size: file.size || 0
          },
          duplicate_candidates: data.results,
          total_duplicates_found: data.total_found,
          popup_config: {
            title: "Potential Duplicate Resumes Found",
            message: `Found ${data.total_found} potential duplicate(s) for this candidate.`,
            show_upload_button: true,
            upload_endpoint: "/api/v2/resumes/upload-and-match",
            cancel_text: "Cancel",
            upload_text: "Upload and Match Anyway"
          }
        })
        setShowDuplicateModal(true)
        setUploadingResume(false)
      } else {
        // No duplicates found, proceed with upload
        console.log('ℹ️ No duplicates found, proceeding with upload')
        await proceedWithUploadAndMatch(file)
      }
      
    } catch (error: any) {
      console.error('Duplicate check failed:', error)
      // If duplicate check fails, proceed with upload anyway
      toast.error('Duplicate check failed, proceeding with upload...')
      await proceedWithUploadAndMatch(file)
    }
  }

  // Handle upload anyway from duplicate modal
  const handleUploadAnyway = async () => {
    setShowDuplicateModal(false)
    await proceedWithUploadAndMatch()
  }

  // Handle cancel from duplicate modal
  const handleCancelUpload = () => {
    setShowDuplicateModal(false)
    setDuplicateData(null)
    setPendingFile(null)
    setUploadingResume(false)
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !job) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <button onClick={() => navigate('/jobs')} className="flex items-center text-gray-600 hover:text-gray-900">
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Jobs
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 mb-2">Job Not Found</h3>
          <p className="text-red-600">
            The job posting you're looking for doesn't exist or has been removed.
            {error && (
              <><br/><span className="text-sm">Error: {(error as any)?.message || String(error)}</span></>
            )}
          </p>
        </div>
      </div>
    )
  }

  // Status badge component
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const statusConfig = {
      active: { icon: CheckCircleIcon, text: 'Active', className: 'bg-green-100 text-green-800' },
      paused: { icon: ClockIcon, text: 'Paused', className: 'bg-yellow-100 text-yellow-800' },
      closed: { icon: ExclamationCircleIcon, text: 'Closed', className: 'bg-red-100 text-red-800' }
      // draft status removed - not displayed
    }

    const config = statusConfig[status as keyof typeof statusConfig]
    // Don't show badge if status is not in config (e.g., draft)
    if (!config) return null

    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.text}
      </span>
    )
  }

  // Processing status badge component - only show when processing
  const ProcessingStatusBadge: React.FC<{ status?: string }> = ({ status }) => {
    // Only show the badge when status is 'processing' or 'pending'
    if (!status || (status !== 'processing' && status !== 'pending')) return null

    const statusConfig = {
      pending: { icon: ClockIcon, text: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
      processing: { icon: ArrowPathIcon, text: 'Processing', className: 'bg-blue-100 text-blue-800 animate-pulse' }
    }

    const config = statusConfig[status as keyof typeof statusConfig]
    if (!config) return null

    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        <Icon className={`w-3 h-3 mr-1 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {config.text}
      </span>
    )
  }

  // Format salary range
  const formatSalary = (min?: number, max?: number, currency: string = 'USD', period?: string) => {
    const periodSuffix = period ? `/${period === 'annual' ? 'year' : period === 'monthly' ? 'month' : 'hour'}` : ''
    if (!min && !max) return 'Not specified'
    if (min && max) return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}${periodSuffix}`
    if (min) return `${currency} ${min.toLocaleString()}+${periodSuffix}`
    if (max) return `Up to ${currency} ${max.toLocaleString()}${periodSuffix}`
    return 'Not specified'
  }

  // Extract AI analysis data (V2 uses ai_processed_requirements)
  const analysis = job.ai_processed_requirements || job.ai_analysis || {}
  const basicInfo = analysis.basic_info || {}
  
  // Parse skills from skills_analysis JSON
  let skills = analysis.skills || []
  if (job.skills_analysis && skills.length === 0) {
    try {
      const skillsData = JSON.parse(job.skills_analysis)
      const extractedSkills: any[] = []
      
      // Extract from technical_skills
      if (skillsData.technical_skills?.items) {
        skillsData.technical_skills.items.forEach((item: any) => {
          const importanceText = (item.importance || '').toLowerCase()
          const importance = ['required', 'must', 'essential', 'critical'].some(word => importanceText.includes(word)) ? 'required' : 'preferred'
          extractedSkills.push({ skill: item.skill, importance })
        })
      }
      
      // Extract from soft_skills
      if (skillsData.soft_skills?.items) {
        skillsData.soft_skills.items.forEach((item: any) => {
          const importanceText = (item.importance || '').toLowerCase()
          const importance = ['required', 'must', 'essential', 'critical'].some(word => importanceText.includes(word)) ? 'required' : 'preferred'
          extractedSkills.push({ skill: item.skill, importance })
        })
      }
      
      // Extract from tools_technologies
      if (skillsData.tools_technologies?.items) {
        skillsData.tools_technologies.items.forEach((item: any) => {
          const importanceText = (item.importance || '').toLowerCase()
          const importance = ['required', 'must', 'essential', 'critical'].some(word => importanceText.includes(word)) ? 'required' : 'preferred'
          extractedSkills.push({ skill: item.tool, importance })  // Note: tools use "tool" field
        })
      }
      
      // Extract from competencies
      if (skillsData.competencies?.items) {
        skillsData.competencies.items.forEach((item: any) => {
          const importanceText = (item.importance || '').toLowerCase()
          const importance = ['required', 'must', 'essential', 'critical'].some(word => importanceText.includes(word)) ? 'required' : 'preferred'
          extractedSkills.push({ skill: item.competency, importance })  // Note: competencies use "competency" field
        })
      }
      
      if (extractedSkills.length > 0) {
        skills = extractedSkills
        console.log('🔍 Extracted Skills Debug:', {
          totalSkills: skills.length,
          requiredSkills: skills.filter((s: any) => s.importance === 'required').length,
          preferredSkills: skills.filter((s: any) => s.importance === 'preferred').length,
          skillsBreakdown: skills.map((s: any) => ({ skill: s.skill, importance: s.importance }))
        })
      }
    } catch (error) {
      console.error('Error parsing skills_analysis:', error)
    }
  }
  
  // Parse experience from experience_assessment JSON
  let experienceReq = analysis.experience_requirements || {}
  if (job.experience_assessment && Object.keys(experienceReq).length === 0) {
    try {
      const expData = JSON.parse(job.experience_assessment)
      const newExpReq: any = {}
      
      // Extract years from required_experience items
      if (expData.required_experience?.items) {
        for (const item of expData.required_experience.items) {
          const desc = (item.description || '') + ' ' + (item.experience || '')
          const yearsMatch = desc.match(/(\d+)[\s-]+(\d+)?\s*years?/i)
          if (yearsMatch) {
            newExpReq.min_years_total = parseInt(yearsMatch[1])
            if (yearsMatch[2]) {
              newExpReq.max_years_total = parseInt(yearsMatch[2])
            }
            break
          }
        }
      }
      
      // Extract leadership
      if (expData.leadership_experience?.items && expData.leadership_experience.items.length > 0) {
        newExpReq.leadership_required = true
      }
      
      // Extract specific areas
      if (expData.industry_experience?.items) {
        newExpReq.specific_experience_areas = expData.industry_experience.items.map(
          (item: any) => item.industry || item.experience
        )
      }
      
      if (Object.keys(newExpReq).length > 0) {
        experienceReq = newExpReq
      }
    } catch (error) {
      console.error('Error parsing experience_assessment:', error)
    }
  }
  
  // Also check job fields directly
  if (job.min_years_experience || job.max_years_experience) {
    experienceReq = {
      ...experienceReq,
      min_years_total: job.min_years_experience || experienceReq.min_years_total,
      max_years_total: job.max_years_experience || experienceReq.max_years_total
    }
  }
  
  const requirements = analysis.requirements || []
  const educationReq = analysis.education_requirements || {}
  const analysisInsights = analysis.analysis_insights || {}
  const matchingCriteria = analysis.matching_criteria || {}
  
  // Debug logging
  console.log('🔍 Job Data Debug:', {
    hasAiAnalysis: !!job.ai_analysis,
    hasAiProcessedRequirements: !!job.ai_processed_requirements,
    analysisKeys: Object.keys(analysis),
    educationReq,
    analysisInsights,
    matchingCriteria,
    jobEducationRequirements: job.education_requirements,
    processingConfidence: job.processing_confidence
  })

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/jobs')} className="flex items-center text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeftIcon className="w-4 h-4 mr-1.5" />
          Back to Jobs
        </button>
        <div className="flex items-center space-x-2">
          <StatusBadge status={job.status} />
          <ProcessingStatusBadge status={job.processing_status} />
        </div>
      </div>

      {/* Main Job Information */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <h1 className="text-xl font-bold text-gray-900">{job.title}</h1>
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-mono">
                  {job.job_id}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                {job.department && (
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="w-3.5 h-3.5 mr-1" />
                    {job.department}
                  </div>
                )}
                {job.client && (
                  <div className="flex items-center">
                    <UserGroupIcon className="w-3.5 h-3.5 mr-1" />
                    {job.client}
                  </div>
                )}
                {job.location && (
                  <div className="flex items-center">
                    <MapPinIcon className="w-3.5 h-3.5 mr-1" />
                    {job.location}
                  </div>
                )}
                <div className="flex items-center">
                  <BriefcaseIcon className="w-3.5 h-3.5 mr-1" />
                  {job.employment_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
                <div className="flex items-center">
                  <UserGroupIcon className="w-3.5 h-3.5 mr-1" />
                  {job.remote_option === 'full' ? 'Full Remote' :
                   job.remote_option === 'hybrid' ? 'Hybrid' : 'On-site'}
                </div>
                {job.source_name && (
                  <div className="flex items-center">
                    <TagIcon className="w-3.5 h-3.5 mr-1" />
                    {job.source_name}
                    {job.source_is_active === false && (
                      <span className="text-xs text-gray-400 ml-1">(Inactive)</span>
                    )}
                  </div>
                )}
              </div>

              {/* Assigned Recruiters */}
              {job.assigned_recruiters && job.assigned_recruiters.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <AssignedRecruitersDisplay
                    recruiters={job.assigned_recruiters}
                    maxAvatars={5}
                    maxNames={3}
                    showManageButton={canManageRecruiters}
                    onManageClick={() => setShowAssignmentModal(true)}
                  />
                </div>
              )}
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  Salary: {formatSalary(job.salary_min, job.salary_max, job.salary_currency, job.salary_period)}
                </div>
                <div className="text-xs text-gray-500">
                  Created: {formatToLocalDate(job.created_at)}
                </div>
              </div>
              {/* Action Dropdown — trigger button hidden entirely when no
                  action inside is available to this role, bug fix
                  2026-08-30 (see hasAnyJobAction above). */}
              {hasAnyJobAction && (
              <div className="relative" ref={actionsDropdownRef}>
                  <button
                      onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                      <span>Actions</span>
                      <ChevronDownIcon className="-mr-1 ml-2 h-4 w-4" />
                  </button>
                  {showActionsDropdown && (
                      <div className="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                          <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                              {/* Edit Action — hidden (not disabled) below senior_recruiter+,
                                  bug fix 2026-08-27 (was deliberately disabled-only per the
                                  now-stale RBAC_UI_IMPLEMENTATION_PLAN.md §5.7, "keeps the menu
                                  shape stable" — superseded by the app-wide hide convention).
                                  Gated on jobs.edit, not canEditAssignments — bug fix
                                  2026-08-29, see canEditJob's definition above. */}
                              {canEditJob && (
                                <button
                                    onClick={() => {
                                        setEditingJob(job);
                                        setShowCreateModal(true);
                                        setShowActionsDropdown(false);
                                    }}
                                    className="w-full text-left flex items-center px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
                                    role="menuitem"
                                >
                                    <PencilIcon className="mr-2 h-4 w-4 text-gray-400" />
                                    <span>Edit</span>
                                </button>
                              )}

                              {/* Download Action */}
                              {canDownloadJD && (
                                  <button
                                      onClick={() => {
                                          handleDownloadJob();
                                          setShowActionsDropdown(false);
                                      }}
                                      className="w-full text-left flex items-center px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
                                      role="menuitem"
                                  >
                                      <ArrowDownTrayIcon className="mr-2 h-4 w-4 text-gray-400" />
                                      <span>Download</span>
                                  </button>
                              )}

                              {/* Reanalyze Action - Admin Only */}
                              {canReprocessJob && (
                                  <button
                                      onClick={() => {
                                          if (jobId) reprocessJobMutation.mutate(jobId);
                                          setShowActionsDropdown(false);
                                      }}
                                      disabled={reprocessJobMutation.isPending}
                                      className="w-full text-left flex items-center px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                      role="menuitem"
                                  >
                                      <ArrowPathIcon className="mr-2 h-4 w-4 text-gray-400" />
                                      <span>Reprocess</span>
                                  </button>
                              )}

                              {/* Delete Action — manager+ (RBAC_UI_IMPLEMENTATION_PLAN.md §5.6.C),
                                  hidden (not disabled) below that tier: destructive enough that a
                                  visible-but-disabled Delete would just invite a confused ticket
                                  from a role that will never be allowed to use it (§5.7). */}
                              {can('jobs.delete') && (
                                  <button
                                      onClick={() => {
                                          handleOpenDeleteModal();
                                          setShowActionsDropdown(false);
                                      }}
                                      disabled={deleteJobMutation.isPending}
                                      className="w-full text-left flex items-center px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                                      role="menuitem"
                                  >
                                      <TrashIcon className="mr-2 h-4 w-4 text-red-400" />
                                      <span>Delete</span>
                                  </button>
                              )}
                          </div>
                      </div>
                  )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          {/* Scrolls sideways inside the bar when the tabs don't fit, instead
              of pushing the whole page wider. */}
          <nav className="flex space-x-6 px-4 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  ref={activeTab === tab.id ? activeTabButtonRef : undefined}
                  onClick={() => {
                    const newTab = tab.id as 'overview' | 'skills' | 'experience' | 'matching' | 'interview' | 'proctoredInterview' | 'liveInterview'
                    setActiveTab(newTab)
                    const newParams = new URLSearchParams(searchParams)
                    newParams.set('tab', newTab)
                    setSearchParams(newParams, { replace: true })
                  }}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2.5 px-1 border-b-2 font-medium text-xs flex items-center space-x-1.5`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-4">
        {activeTab === 'overview' && isTalentosJob && (
          <TalentosJobProfileTab
            jobId={job!.id}
            jobReadableId={job!.job_id}
            title={job!.title}
            client={job!.client}
            department={job!.department}
            location={job!.location}
            employmentType={job!.employment_type}
            remoteOption={job!.remote_option}
            benefits={job!.benefits}
            formMinYearsExperience={job!.min_years_experience}
            formMaxYearsExperience={job!.max_years_experience}
            originalFilename={job!.original_filename}
          />
        )}

        {activeTab === 'overview' && !isTalentosJob && (
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {/* AI Analysis Section - Always Display */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className="px-4 py-2.5 border-b border-blue-200">
                <div className="flex items-center">
                  <CpuChipIcon className="w-5 h-5 text-blue-600 mr-2" />
                  <h2 className="text-base font-semibold text-gray-900">Analysis Results</h2>
                </div>
              </div>

              <div className="p-3 space-y-3">
                {/* Skills Analysis - Always Display */}
                <CollapsibleCard title="Skills Analysis" icon={<LightBulbIcon className="w-4 h-4 text-yellow-500" />} defaultOpen compact>
                  {skills.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <h4 className="text-xs font-medium text-green-700 mb-1.5">Required Skills</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {skills.filter((skill: any) => skill.importance === 'required').map((skill: any, index: number) => (
                            <span key={index} className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">
                              {skill.skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-blue-700 mb-1.5">Preferred Skills</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {skills.filter((skill: any) => skill.importance === 'preferred').map((skill: any, index: number) => (
                            <span key={index} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                              {skill.skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Not Available</p>
                  )}
                </CollapsibleCard>

                {/* Experience Requirements - Always Display */}
                <CollapsibleCard title="Experience" icon={<ChartBarIcon className="w-4 h-4 text-purple-500" />} defaultOpen compact>
                  {(experienceReq.min_years_total || experienceReq.max_years_total) ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <span className="text-xs text-gray-600">Total Experience</span>
                        <p className="text-sm font-medium">
                          {experienceReq.min_years_total && experienceReq.max_years_total
                            ? `${experienceReq.min_years_total}-${experienceReq.max_years_total} years`
                            : experienceReq.min_years_total
                            ? `${experienceReq.min_years_total}+ years`
                            : experienceReq.max_years_total
                            ? `Up to ${experienceReq.max_years_total} years`
                            : 'Not specified'
                          }
                        </p>
                      </div>
                      {experienceReq.leadership_required && (
                        <div>
                          <span className="text-xs text-gray-600">Leadership</span>
                          <p className="text-sm font-medium text-orange-600">Required</p>
                        </div>
                      )}
                      {experienceReq.specific_experience_areas?.length > 0 && (
                        <div>
                          <span className="text-xs text-gray-600">Specific Areas</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {experienceReq.specific_experience_areas.map((area: string, index: number) => (
                              <span key={index} className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs">
                                {area}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Not Available</p>
                  )}
                </CollapsibleCard>

                {/* Education Requirements - Always Display */}
                <CollapsibleCard title="Education Requirements" icon={<AcademicCapIcon className="w-4 h-4 text-indigo-500" />} defaultOpen compact>
                  {(educationReq.degree_level || educationReq.field_of_study?.length > 0) ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {educationReq.degree_level && (
                        <div>
                          <span className="text-xs text-gray-600">Degree Level</span>
                          <p className="text-sm font-medium capitalize">{educationReq.degree_level}</p>
                        </div>
                      )}
                      {educationReq.field_of_study?.length > 0 && (
                        <div>
                          <span className="text-xs text-gray-600">Field of Study</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {educationReq.field_of_study.map((field: string, index: number) => (
                              <span key={index} className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded text-xs">
                                {field}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Not Available</p>
                  )}
                </CollapsibleCard>

                {/* Quality Assessment - Always Display for Admin */}
                {isAdminDisplayOnly && (
                  <CollapsibleCard title="Quality Assessment" icon={<StarIcon className="w-4 h-4 text-amber-500" />} defaultOpen compact>
                    {(analysisInsights.clarity_score || analysisInsights.completeness_score || analysisInsights.attractiveness_score || analysisInsights.inclusivity_score || analysisInsights.complexity_score || analysisInsights.competitiveness_score) ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {analysisInsights.clarity_score && (
                          <div>
                            <span className="text-xs text-gray-600">Clarity Score</span>
                            <div className="flex items-center mt-1">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-blue-500 h-2 rounded-full"
                                  style={{ width: `${analysisInsights.clarity_score * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium">{Math.round(analysisInsights.clarity_score * 100)}%</span>
                            </div>
                          </div>
                        )}
                        {analysisInsights.completeness_score && (
                          <div>
                            <span className="text-xs text-gray-600">Completeness</span>
                            <div className="flex items-center mt-1">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-green-500 h-2 rounded-full"
                                  style={{ width: `${analysisInsights.completeness_score * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium">{Math.round(analysisInsights.completeness_score * 100)}%</span>
                            </div>
                          </div>
                        )}
                        {analysisInsights.attractiveness_score && (
                          <div>
                            <span className="text-xs text-gray-600">Attractiveness</span>
                            <div className="flex items-center mt-1">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-purple-500 h-2 rounded-full"
                                  style={{ width: `${analysisInsights.attractiveness_score * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium">{Math.round(analysisInsights.attractiveness_score * 100)}%</span>
                            </div>
                          </div>
                        )}
                        {analysisInsights.attractiveness_score && (
                          <div>
                            <span className="text-xs text-gray-600">Attractiveness</span>
                            <div className="flex items-center mt-1">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-purple-500 h-2 rounded-full"
                                  style={{ width: `${analysisInsights.attractiveness_score * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium">{Math.round(analysisInsights.attractiveness_score * 100)}%</span>
                            </div>
                          </div>
                        )}
                        {analysisInsights.inclusivity_score && (
                          <div>
                            <span className="text-xs text-gray-600">Inclusivity</span>
                            <div className="flex items-center mt-1">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-indigo-500 h-2 rounded-full"
                                  style={{ width: `${analysisInsights.inclusivity_score * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium">{Math.round(analysisInsights.inclusivity_score * 100)}%</span>
                            </div>
                          </div>
                        )}
                        {analysisInsights.complexity_score && (
                          <div>
                            <span className="text-xs text-gray-600">Complexity</span>
                            <div className="flex items-center mt-1">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-orange-500 h-2 rounded-full"
                                  style={{ width: `${analysisInsights.complexity_score * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium">{Math.round(analysisInsights.complexity_score * 100)}%</span>
                            </div>
                          </div>
                        )}
                        {analysisInsights.competitiveness_score && (
                          <div>
                            <span className="text-xs text-gray-600">Competitiveness</span>
                            <div className="flex items-center mt-1">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-red-500 h-2 rounded-full"
                                  style={{ width: `${analysisInsights.competitiveness_score * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium">{Math.round(analysisInsights.competitiveness_score * 100)}%</span>
                            </div>
                          </div>
                        )}
                        {analysisInsights.market_demand_level && (
                          <div>
                            <span className="text-xs text-gray-600">Market Demand</span>
                            <p className="text-sm font-medium capitalize mt-1">{analysisInsights.market_demand_level}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Not Available</p>
                    )}
                  </CollapsibleCard>
                )}

                {/* Matching Configuration - Always Display for Admin */}
                {isAdminDisplayOnly && (
                  <CollapsibleCard title="Matching Configuration" defaultOpen compact>
                    {matchingCriteria.minimum_match_threshold ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div>
                          <span className="text-gray-600">Match Threshold</span>
                          <p className="text-sm font-medium">{Math.round(matchingCriteria.minimum_match_threshold * 100)}%</p>
                        </div>
                        {matchingCriteria.experience_weight && (
                          <div>
                            <span className="text-gray-600">Experience Weight</span>
                            <p className="text-sm font-medium">{Math.round(matchingCriteria.experience_weight * 100)}%</p>
                          </div>
                        )}
                        {matchingCriteria.education_weight && (
                          <div>
                            <span className="text-gray-600">Education Weight</span>
                            <p className="text-sm font-medium">{Math.round(matchingCriteria.education_weight * 100)}%</p>
                          </div>
                        )}
                        {matchingCriteria.location_weight && (
                          <div>
                            <span className="text-gray-600">Location Weight</span>
                            <p className="text-sm font-medium">{Math.round(matchingCriteria.location_weight * 100)}%</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Not Available</p>
                    )}
                  </CollapsibleCard>
                )}
              </div>
            </div>

        {/* Job Details Section — Description/Responsibilities/Requirements/
            Benefits grouped under one "Job Details" accordion; opening it
            reveals all of them together, same as before. */}
        <CollapsibleCard title="Job Details" icon={<DocumentTextIcon className="w-4 h-4 text-blue-600" />} defaultOpen compact>
          <div className="space-y-4">
            {/* Job Description */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Description</h3>
              <div className="prose prose-sm max-w-none text-gray-700">
                {job.description.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-2">{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Responsibilities */}
            {job.responsibilities && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Responsibilities</h3>
                <div className="prose prose-sm max-w-none text-gray-700">
                  <ul className="space-y-1.5">
                    {job.responsibilities.split('\n').filter(item => item.trim()).map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                        <span className="text-gray-700">{item.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Requirements */}
            {job.requirements && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Requirements</h3>
                <div className="prose prose-sm max-w-none text-gray-700">
                  <ul className="space-y-1.5">
                    {job.requirements.split('\n').filter(item => item.trim()).map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-green-500 mt-1 flex-shrink-0">•</span>
                        <span className="text-gray-700">{item.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Benefits */}
            {job.benefits && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Benefits</h3>
                <div className="prose prose-sm max-w-none text-gray-700">
                  <ul className="space-y-1.5">
                    {job.benefits.split('\n').filter(item => item.trim()).map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-purple-500 mt-1 flex-shrink-0">•</span>
                        <span className="text-gray-700">{item.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </CollapsibleCard>
          </div>
        )}

        {activeTab === 'skills' && (
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-4 py-2.5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900 flex items-center">
                  <CpuChipIcon className="w-5 h-5 text-blue-600 mr-2" />
                  Required Skills
                </h2>
                {job.analysis_generated_at && (
                  <div className="text-xs text-gray-500">
                    Generated: {new Date(job.analysis_generated_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            <div className="p-3 max-h-[65vh] overflow-y-auto pr-1">
              {job.skills_analysis ? (
                <div className="max-w-none">
                  <div className="prose prose-sm max-w-none">
                    {renderStructuredSkillsAnalysis(job.skills_analysis)}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <CpuChipIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Skills Analysis Not Available</h3>
                  <p className="text-gray-500">
                    Skills analysis will be generated automatically when this job is created or updated.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'experience' && (
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <ChartBarIcon className="w-6 h-6 text-blue-600 mr-3" />
                  Experience
                </h2>
                {job.analysis_generated_at && (
                  <div className="text-sm text-gray-500">
                    Generated: {new Date(job.analysis_generated_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6 max-h-[65vh] overflow-y-auto pr-1">
              {job.experience_assessment ? (
                <div className="max-w-none">
                  <div className="prose prose-sm max-w-none">
                    {renderStructuredExperienceAssessment(job.experience_assessment)}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Experience Not Available</h3>
                  <p className="text-gray-500">
                    Experience will be generated automatically when this job is created or updated.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}


        {activeTab === 'matching' && (
          <div className="space-y-6">
            {/* Candidate Search Section */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-1.5">
                  <UserGroupIcon className="w-4 h-4 text-blue-600" />
                  <span>Search Candidates to Apply</span>
                </h3>
              </div>

              <div className="space-y-3">
                {/* Search Section */}
                <div className="flex space-x-2">
                  <div className="flex-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Search candidates by name, email, phone, id, title, or skills..."
                      className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={handleSearch}
                    disabled={!searchTerm.trim()}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5"
                  >
                    <MagnifyingGlassIcon className="w-3.5 h-3.5" />
                    <span>Search</span>
                  </button>
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center space-x-1.5"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                      <span>Clear</span>
                    </button>
                  )}
                </div>

                {/* Search Results */}
                {searchQuery && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        {resumesLoading ? 'Searching...' : `${resumes.length} candidates found`}
                      </span>
                      {selectedCandidates.length > 0 && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">
                            {selectedCandidates.length} selected
                          </span>
                          <button
                            onClick={handleCompareCandidates}
                            disabled={comparisonLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                          >
                            {comparisonLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>Processing...</span>
                              </>
                            ) : (
                              <span>Apply Selected</span>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {resumesLoading ? (
                        <div className="flex justify-center py-8">
                          <LoadingSpinner size="md" />
                        </div>
                      ) : resumes.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <UserGroupIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p>No candidates found matching your search</p>
                          <p className="text-sm">Try different search terms</p>
                        </div>
                      ) : (
                        resumes.map((resume: any) => {
                          const isAlreadyAnalyzed = alreadyAnalyzedCandidates.includes(resume.id)
                          const isSelected = selectedCandidates.includes(resume.id)
                          const isBlacklisted = resume.blacklist
                          const isSelectable = !isAlreadyAnalyzed && !isBlacklisted
                          
                          return (
                            <div
                              key={resume.id}
                              className={`p-3 border rounded-lg transition-colors ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-50'
                                  : isAlreadyAnalyzed
                                  ? 'border-gray-200 bg-gray-50 opacity-60'
                                  : isBlacklisted
                                  ? 'border-red-200 bg-red-50 opacity-60'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div 
                                  className={`flex items-center space-x-3 flex-1 ${
                                    isSelectable ? 'cursor-pointer' : 'cursor-not-allowed'
                                  }`}
                                  onClick={() => {
                                    if (isSelectable || isSelected) {
                                      handleCandidateSelect(resume.id)
                                    }
                                  }}
                                >
                                  <div className={`w-4 h-4 border-2 rounded ${
                                    isSelected
                                      ? 'border-blue-500 bg-blue-500'
                                      : isAlreadyAnalyzed
                                      ? 'border-gray-300 bg-gray-200'
                                      : isBlacklisted
                                      ? 'border-red-300 bg-red-200'
                                      : 'border-gray-300'
                                  }`}>
                                    {isSelected && (
                                      <CheckIcon className="w-3 h-3 text-white" />
                                    )}
                                    {isAlreadyAnalyzed && (
                                      <XMarkIcon className="w-3 h-3 text-gray-500" />
                                    )}
                                    {isBlacklisted && (
                                      <ExclamationTriangleIcon className="w-3 h-3 text-red-500" />
                                    )}
                                  </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    {resume.candidate_id && (
                                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-mono">
                                        {resume.candidate_id}
                                      </span>
                                    )}
                                    <h4 className={`font-medium ${
                                      isAlreadyAnalyzed || isBlacklisted ? 'text-gray-500' : 'text-gray-900'
                                    }`}>
                                      {resume.candidate_name}
                                    </h4>
                                    {isAlreadyAnalyzed && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                        <CheckIcon className="w-3 h-3 mr-1" />
                                        Already Selected
                                      </span>
                                    )}
                                    {isBlacklisted && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                                        <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                                        Blacklisted
                                      </span>
                                    )}
                                  </div>
                                  <div className={`text-sm space-y-1 ${
                                    isBlacklisted ? 'text-gray-500' : 'text-gray-600'
                                  }`}>
                                    {resume.professional_title && <div className="flex items-center space-x-1">
                                      <BriefcaseIcon className="w-4 h-4" />
                                      <span className="font-medium">{resume.professional_title}</span>
                                    </div>}
                                    {resume.candidate_email && <div className="flex items-center space-x-1">
                                      <EnvelopeIcon className={`w-4 h-4 ${isBlacklisted ? 'text-gray-400' : 'text-blue-600'}`} />
                                      <span className={`font-medium ${isBlacklisted ? 'text-gray-500' : 'text-blue-600'}`}>{resume.candidate_email}</span>
                                    </div>}
                                    {resume.candidate_phone && <div className="flex items-center space-x-1">
                                      <PhoneIcon className={`w-4 h-4 ${isBlacklisted ? 'text-gray-400' : 'text-green-600'}`} />
                                      <span className={`font-medium ${isBlacklisted ? 'text-gray-500' : 'text-green-600'}`}>{formatPhoneNumber(resume.candidate_phone)}</span>
                                    </div>}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Right side: Experience and View Button */}
                              <div className="flex items-center space-x-4">
                                {resume.total_years_experience && (
                                  <div className={`text-sm ${isBlacklisted ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {resume.total_years_experience} years exp
                                  </div>
                                )}
                                
                                {/* View Resume Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation() // Prevent triggering checkbox
                                    handleViewCandidateProfile(resume.id)
                                  }}
                                  className="px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center space-x-1 bg-blue-600 text-white hover:bg-blue-700"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                  <span>View</span>
                                </button>
                              </div>
                            </div>
                          </div>
                          )
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Analysis Results - Always Visible */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">Analysis Results</h3>
                {canApplyResume && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openBulkApply(jobId!, job.title)}
                      className="px-3 py-1.5 bg-white text-green-700 border border-green-600 rounded-lg hover:bg-green-50 transition-colors flex items-center space-x-1.5 text-xs"
                      title="Upload resumes and apply them to this job"
                    >
                      <CloudArrowUpIcon className="w-3.5 h-3.5" />
                      <span>Apply Resume(s)</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Summary Insights */}
              {comparisonResults?.summary_insights ? (
                <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-1.5">Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <div>
                      <span className="text-blue-700 font-medium">Total Candidates:</span>
                      <span className="ml-2">{comparisonResults.summary_insights.total_candidates_compared}</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Average Score:</span>
                      {/* Already a percent, already rounded to 1 decimal server-side
                          (matching.py) -- Math.round() here was re-rounding an
                          already-correct value, same bug as the per-card scores. */}
                      <span className="ml-2">{comparisonResults.summary_insights.average_match_score}%</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Top Candidate:</span>
                      {/* top_candidate.overall_score IS that candidate's own
                          display_percent (see matching.py's comment on it) --
                          must show the exact same number as their card, not a
                          separately-rounded one. */}
                      <span className="ml-2">{comparisonResults.summary_insights.top_candidate?.name} ({comparisonResults.summary_insights.top_candidate?.overall_score}%)</span>
                    </div>
                  </div>
                </div>
              ) : isInitialMatchLoading ? (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg animate-pulse">
                  <h4 className="text-sm font-medium text-gray-700 mb-1.5">Summary</h4>
                  <p className="text-xs text-gray-400">Loading comparison summary...</p>
                </div>
              ) : (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-1.5">Summary</h4>
                  <p className="text-xs text-gray-600">No comparison data available. Select candidates and run comparison to see analysis results.</p>
                </div>
              )}
              
              {/* Filter Panel */}
              {comparisonResults?.comparison_results && comparisonResults.comparison_results.length > 0 && (
                <FilterPanel
                  filterState={filterState}
                  uniqueUsers={uniqueUsers}
                  uniqueStatuses={uniqueStatuses}
                  onAssignedUserChange={(userId) => {
                    console.log('Assigned User Changed:', userId);
                    setFilterState(prev => ({ ...prev, assignedUserId: userId }));
                  }}
                  onStatusChange={(status) => {
                    console.log('Status Changed:', status);
                    setFilterState(prev => ({ ...prev, status }));
                  }}
                  onScoreRangeChange={(range) => {
                    console.log('Score Range Changed:', range);
                    setFilterState(prev => ({ ...prev, scoreRange: range }));
                  }}
                  onTogglePanel={togglePanel}
                  onClearFilters={clearFilters}
                  activeFilterCount={activeFilterCount}
                />
              )}
              
              {/* Result Count Display */}
              {comparisonResults?.comparison_results && comparisonResults.comparison_results.length > 0 && (
                <ResultCountDisplay
                  totalCount={comparisonResults?.comparison_results?.length || 0}
                  filteredCount={filteredMatches.length}
                  hasActiveFilters={hasFilters}
                  entityType="candidates"
                />
              )}
              
                {/* Comparison Results Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                  <div className="p-4">
                    <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                      {isInitialMatchLoading ? (
                        <div className="px-6 py-12 flex items-center justify-center">
                          <LoadingSpinner size="lg" />
                        </div>
                      ) : comparisonResults?.comparison_results && comparisonResults.comparison_results.length > 0 ? (
                        paginatedMatches.map((result: any, index: number) => (
                            <div key={result?.resume_id || result?.candidate_id || index} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow bg-white flex items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                              {/* Name row */}
                              <div className="flex items-center space-x-2 mb-1.5">
                                  <UserIcon className="h-4 w-4 text-gray-600" />
                                  <span className="text-sm font-semibold text-gray-900">{result.candidate_name}</span>
                                  {canEditAssignments && (
                                    <ViewAuditTrailButton
                                      onClick={() =>
                                        handleViewAuditTrail(
                                          'job_match',
                                          result.id,
                                          result.candidate_name
                                        )
                                      }
                                    />
                                  )}

                                  {result.blacklist && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                                      Blacklisted
                                    </span>
                                  )}
                              </div>

                              {/* Assignment Line: Assigned to Badge */}
                              <div className="flex items-center space-x-2 mb-1.5">
                                <span className="text-xs text-gray-600">Assigned to:</span>
                                <AssignmentBadge
                                  userName={result.matched_by_name}
                                  assignmentType="manual"
                                  onClick={canEditAssignments ? () =>
                                    handleEditAssignment(
                                      'job_match',
                                      result.id,
                                      result.candidate_name,
                                      result.matched_by_id,
                                      result.matched_by_name
                                    ) : undefined
                                  }
                                  clickable={canEditAssignments}
                                />
                              </div>

                              {/* Line 2: Job Title • Candidate ID */}
                              <div className="text-xs text-gray-600 mb-1.5">
                                {result.professional_title || 'No title'} • {result.candidate_id || 'N/A'}
                              </div>

                              {/* Line 3: Matching Status Badge */}
                              <div className="mb-2">
                                <MatchingStatusBadge
                                  status={result.status === 'completed' ? 'completed' :
                                         result.status === 'processing' ? 'processing' :
                                         result.status === 'pending' ? 'pending' :
                                         result.status === 'failed' ? 'failed' : 'unknown'}
                                  retryCount={result.retry_count}
                                />
                              </div>

                              {/* Line 4: Skills, Experience, Education */}
                              <div className="flex items-center space-x-4 text-xs">
                                <div className="flex items-center space-x-2">
                                  <span className="text-green-600">✅</span>
                                  <span className="text-gray-700">
                                    <span className="font-medium">Skills:</span> {result?.detailed_analysis?.skills_breakdown?.skills_matched_count || 0}/{result?.detailed_analysis?.skills_breakdown?.total_required_skills || 0}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-orange-600">⭐</span>
                                  <span className="text-gray-700">
                                    <span className="font-medium">Experience:</span> {result?.total_years_experience || 0} yrs
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-purple-600">🎓</span>
                                  <span className="text-gray-700">
                                    <span className="font-medium">Education:</span> {result?.detailed_analysis?.education_breakdown?.degree_match ? 'Degree Match' : (getHighestDegreeAndField(result?.detailed_analysis?.education_summary) || 'Not evaluated')}
                                  </span>
                                </div>
                              </div>

                              {/* Stage Acknowledgements — renders nothing when no rounds
                                  are resolved yet for this candidate. */}
                              <StageAcknowledgementPanel jobId={jobId!} resumeId={result.resume_id} />
                              </div>

                              {/* Score + Status Dropdown + Actions — its own flex child at
                                  the card level (not nested in the name row) so it's
                                  vertically centered against the WHOLE card via the card's
                                  own items-center, not just against the name row. */}
                              <div className="flex items-center space-x-4 flex-shrink-0">
                                  {/* TalentOS score, falling back to the
                                      original score for any candidate TalentOS
                                      has not scored yet. One source per
                                      candidate — never both at once. Wrapped
                                      in a small bordered box (UI-only change)
                                      so the score reads as its own clickable
                                      card rather than bare text sitting next
                                      to the status dropdown. */}
                                  <div
                                    onClick={() =>
                                      setSelectedCandidateForDetails(
                                        selectedCandidateForDetails === result.candidate_id ? null : result.candidate_id
                                      )
                                    }
                                    className="flex flex-col items-center justify-center px-3 pt-1 pb-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer"
                                  >
                                    <TalentosScoreCell
                                      summary={talentosMatches.byResumeId.get(result.resume_id)}
                                      className="text-lg font-bold"
                                      onClick={() =>
                                        setSelectedCandidateForDetails(
                                          selectedCandidateForDetails === result.candidate_id ? null : result.candidate_id
                                        )
                                      }
                                      fallback={
                                        <span
                                          className={
                                            result.status === 'failed'
                                              ? 'text-sm font-semibold text-gray-400 cursor-pointer hover:underline'
                                              : 'text-lg font-bold text-blue-600 cursor-pointer hover:underline'
                                          }
                                          role="button"
                                          tabIndex={0}
                                          onClick={() =>
                                            setSelectedCandidateForDetails(
                                              selectedCandidateForDetails === result.candidate_id ? null : result.candidate_id
                                            )
                                          }
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault()
                                              setSelectedCandidateForDetails(
                                                selectedCandidateForDetails === result.candidate_id ? null : result.candidate_id
                                              )
                                            }
                                          }}
                                          title={
                                            result.status === 'failed'
                                              ? 'Resume analysis failed — no score to show'
                                              : 'Click to view detailed scores'
                                          }
                                        >
                                          {/* A failed analysis never produced a real score --
                                              match_score defaults to 0 in that case, and showing
                                              "0%" reads as "we measured this candidate and they
                                              scored zero", which is false. The "Analysis Failed"
                                              badge below already says what actually happened. */}
                                          {result.status === 'failed' ? '—' : `${toDisplayPercent(result?.match_score ?? 0)}%`}
                                        </span>
                                      }
                                    />
                                  </div>
                                  {/* Hidden (not disabled) without candidateStatus.change,
                                      bug fix 2026-08-27 — only this control is gated; the
                                      surrounding row and the results list/query itself are
                                      untouched. */}
                                  {canChangeCandidateStatus && (
                                    <ApplicationStatusDropdown
                                      currentStatus={applicationStatuses[result.resume_id] || result.candidate_status_id || null}
                                      oldStatus={result.application_status}
                                      matchCreatedAt={result.match_created_at}
                                      jobId={jobId!}
                                      resumeId={result.resume_id}
                                      onStatusChange={(newStatus, comment) => handleApplicationStatusChange(result.resume_id, newStatus, comment)}
                                    />
                                  )}
                                  <div className="relative">
                                    <button
                                      onClick={(e) => {
                                        const buttonRect = e.currentTarget.getBoundingClientRect()
                                        const viewportHeight = window.innerHeight
                                        const viewportWidth = window.innerWidth
                                        const dropdownHeight = 320
                                        const dropdownWidth = 280
                                        const spaceBelow = viewportHeight - buttonRect.bottom
                                        const spaceAbove = buttonRect.top

                                        let leftPosition = buttonRect.left - 200
                                        if (leftPosition < 10) leftPosition = 10
                                        if (leftPosition + dropdownWidth > viewportWidth - 10) {
                                          leftPosition = viewportWidth - dropdownWidth - 10
                                        }

                                        setButtonPosition({
                                          left: leftPosition,
                                          top: buttonRect.top,
                                          bottom: buttonRect.bottom
                                        })

                                        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                                          setDropdownPosition('above')
                                        } else if (spaceAbove < dropdownHeight && spaceBelow > dropdownHeight) {
                                          setDropdownPosition('below')
                                        } else {
                                          setDropdownPosition(spaceAbove > 100 ? 'above' : 'below')
                                        }

                                        setShowCandidateActionsDropdown(showCandidateActionsDropdown === result.candidate_id ? null : result.candidate_id)
                                      }}
                                      className="inline-flex items-center justify-center w-7 h-7 border border-gray-300 shadow-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      aria-label="Candidate actions"
                                    >
                                      <EllipsisVerticalIcon className="w-4 h-4" />
                                    </button>

                                    {showCandidateActionsDropdown === result.candidate_id && (
                                      <>
                                        <div className="fixed inset-0 z-[9998]" onClick={() => setShowCandidateActionsDropdown(null)} />
                                        <div className="fixed z-[99999] w-80 rounded-lg shadow-xl bg-white border border-gray-200 overflow-hidden"
                                             style={{
                                               maxHeight: '320px',
                                               minWidth: '280px',
                                               left: `${buttonPosition?.left || 0}px`,
                                               ...(dropdownPosition === 'above'
                                                 ? { bottom: `${window.innerHeight - (buttonPosition?.top || 0) + 8}px` }
                                                 : { top: `${(buttonPosition?.bottom || 0) + 8}px` }
                                               )
                                             }}>
                                          <div className="py-1 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                            {/* Retry, for a row whose analysis (not just matching) failed --
                                                same resumeId, no re-upload, no duplicate-detection prompt.
                                                Reprocess comparison below only retries matching, which
                                                silently fails again if the résumé itself never analysed. */}
                                            {canNewMatch && result.status === 'failed' && (
                                            <button
                                              onClick={() => {
                                                handleRetryAnalysis(result.resume_id)
                                                setShowCandidateActionsDropdown(null)
                                              }}
                                              disabled={reprocessingRecords.has(result.resume_id)}
                                              className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                                            >
                                              {reprocessingRecords.has(result.resume_id) ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-3"></div>
                                              ) : (
                                                <ArrowPathIcon className="h-4 w-4 mr-3 text-red-600" />
                                              )}
                                              <span>Retry</span>
                                            </button>
                                            )}

                                            {/* Hidden (not disabled) without matching.newMatch — bug fix
                                                2026-08-30, found ungated on both frontend and backend. */}
                                            {canNewMatch && (
                                            <button
                                              onClick={() => {
                                                handleReprocessRecord(result.resume_id)
                                                setShowCandidateActionsDropdown(null)
                                              }}
                                              disabled={reprocessingRecords.has(result.resume_id)}
                                              className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                                            >
                                              {reprocessingRecords.has(result.resume_id) ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-3"></div>
                                              ) : (
                                                <svg className="h-4 w-4 mr-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                              )}
                                              <span>Reprocess comparison</span>
                                            </button>
                                            )}

                                            {/* Hidden (not disabled) without interviews.create — bug fix
                                                2026-08-30, matching ResumeDetailPage.tsx's equivalent
                                                dropdown, which already had this exact check. */}
                                            {/* Matching-score threshold check disabled: && ((result?.match_score ?? 0) * 100 >= 40) */}
                                            {can('interviews.create') && !result.blacklist && (
                                              <AIInterviewButton
                                                resumeId={result.resume_id}
                                                jobId={jobId!}
                                                matchingPercentage={(result?.match_score ?? 0) * 100}
                                                threshold={40}
                                                candidateName={result.candidate_name}
                                                jobTitle={job?.title || ''}
                                                onDropdownClose={() => setShowCandidateActionsDropdown(null)}
                                                showLabel={true}
                                                origin="job"
                                              />
                                            )}

                                            <button
                                              onClick={() => {
                                                setSelectedCandidateForDetails(
                                                  selectedCandidateForDetails === result.candidate_id ? null : result.candidate_id
                                                )
                                                setShowCandidateActionsDropdown(null)
                                              }}
                                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            >
                                              <ChartBarIcon className="h-4 w-4 mr-3 text-blue-600" />
                                              <span>View detailed scores</span>
                                            </button>

                                            {/* Hidden (not disabled) without resumes.viewDetails — bug fix
                                                2026-08-30: this opens a popup backed by the same
                                                GET /resumes/{id}/analysis route that key already gates,
                                                previously dead-ending into an error state instead of
                                                hiding for a role without it. */}
                                            {canViewCandidateDetails && (
                                            <button
                                              onClick={() => {
                                                handleViewCandidateProfile(result.resume_id)
                                                setShowCandidateActionsDropdown(null)
                                              }}
                                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            >
                                              <UserIcon className="h-4 w-4 mr-3 text-purple-600" />
                                              <span>View candidate profile</span>
                                            </button>
                                            )}

                                            {/* Hidden (not disabled) without resumes.downloadOriginal —
                                                bug fix 2026-08-30: calls the same resumesApi.download()
                                                already gated by this key elsewhere. */}
                                            {canDownloadOriginal && (
                                            <button
                                              onClick={() => {
                                                downloadResumeMutation.mutate({
                                                  resumeId: result.resume_id,
                                                  candidateName: result.candidate_name,
                                                  jobTitle: job ? job.title : 'Job',
                                                })
                                                setShowCandidateActionsDropdown(null)
                                              }}
                                              disabled={downloadResumeMutation.isPending}
                                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {downloadResumeMutation.isPending ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-3"></div>
                                              ) : (
                                                <ArrowDownTrayIcon className="h-4 w-4 mr-3 text-green-600" />
                                              )}
                                              <span>Download Resume</span>
                                            </button>
                                            )}

                                            {/* Format with Client Template — this job's own client, no picker
                                                (see formatWithTemplateMutation above for why). Hidden entirely
                                                when the job has no client set, or without resumes.downloadFormatted. */}
                                            {canDownloadFormatted && job && job.client && (
                                            <>
                                            <button
                                              onClick={() => {
                                                formatWithTemplateMutation.mutate({
                                                  resumeId: result.resume_id,
                                                  candidateName: result.candidate_name,
                                                  withLogo: false,
                                                })
                                                setShowCandidateActionsDropdown(null)
                                              }}
                                              disabled={isFormatWithTemplateInFlight(result.resume_id, false)}
                                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {isFormatWithTemplateInFlight(result.resume_id, false) ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                                              ) : (
                                                <DocumentTextIcon className="h-4 w-4 mr-3 text-blue-600" />
                                              )}
                                              <span>Formatted Resume</span>
                                            </button>
                                            <button
                                              onClick={() => {
                                                formatWithTemplateMutation.mutate({
                                                  resumeId: result.resume_id,
                                                  candidateName: result.candidate_name,
                                                  withLogo: true,
                                                })
                                                setShowCandidateActionsDropdown(null)
                                              }}
                                              disabled={isFormatWithTemplateInFlight(result.resume_id, true)}
                                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {isFormatWithTemplateInFlight(result.resume_id, true) ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                                              ) : (
                                                <DocumentTextIcon className="h-4 w-4 mr-3 text-blue-600" />
                                              )}
                                              <span>Formatted Resume with Logo</span>
                                            </button>
                                            </>
                                            )}
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center space-y-3">
                              {comparisonResults?.status === 'queued' || comparisonResults?.status === 'processing' ? (
                                <>
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                  <p className="text-sm text-gray-600">
                                    {comparisonResults.status === 'queued' ? 'Comparisons queued for processing...' : 'Processing comparisons...'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    This may take a few minutes. Results will appear automatically when ready.
                                  </p>
                                </>
                              ) : comparisonResults?.status === 'no_results' ? (
                                <>
                                  <ChartBarIcon className="h-12 w-12 text-gray-400" />
                                  <p className="text-sm text-gray-600">No comparison results found</p>
                                  <p className="text-xs text-gray-500">Select candidates and click "Compare Selected" to start</p>
                                </>
                              ) : (
                                <>
                                  <ChartBarIcon className="h-12 w-12 text-gray-400" />
                                  <p className="text-sm text-gray-600">No comparison results yet</p>
                                  <p className="text-xs text-gray-500">Select candidates and click "Compare Selected" to start</p>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                  
                  {/* Pagination Controls */}
                  {filteredMatches.length > itemsPerPage && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
                      <div className="text-sm text-gray-700">
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredMatches.length)} of {filteredMatches.length} results
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-700">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
          </div>
        )}
      </div>

      {/* Detailed View Modal */}
      {selectedCandidateForDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[rgba(15,23,42,0.45)] p-6">
          <div className="mx-auto flex w-full max-w-[1280px] max-h-[92vh] flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_26px_70px_rgba(15,23,42,0.3)]">
            {/* Fixed header -- a flex-column sibling of the scrollable body
                below, not `position: sticky`: it structurally never enters
                the scrolling region at all, so it can't be scrolled past.
                Matches the approved design's own card layout exactly
                (flex-column, overflow:hidden, header/body/footer as
                separate flex children). */}
            <div className="flex-shrink-0 border-b border-gray-100 px-5 py-4 flex items-start justify-between">
                <div>
                  <h3 className="text-lg text-gray-900 flex items-center gap-1.5">
                    <span className="font-semibold">{job?.title || 'Job'}</span>
                    <span className="font-normal text-gray-400">vs</span>
                    <span className="font-semibold">
                      {comparisonResults?.comparison_results?.find(
                        (r: any) => r.candidate_id === selectedCandidateForDetails
                      )?.candidate_name || 'Candidate'}
                    </span>
                    <Tooltip content="Comparison between job requirements and candidate's resume" position="bottom" theme="dark" width="25rem">
                      <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-blue-600 cursor-help" />
                    </Tooltip>
                  </h3>
                </div>
                {/* Download/Approve/Deny/Close now live in the report's own
                    action bar at the bottom (see TalentosMatchReport.tsx) --
                    this X is a secondary, always-available way to close. */}
                <button
                  onClick={() => setSelectedCandidateForDetails(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
            </div>

            {/* Scrollable body -- only this region scrolls; header above and
                the report's own action bar (sticky within here) stay fixed. */}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">

              {(() => {
                const result = comparisonResults?.comparison_results?.find(
                  (r: any) => r.candidate_id === selectedCandidateForDetails
                )
                if (!result) return null

                // ── TalentOS ──────────────────────────────────────────────
                // The TalentOS report replaces the breakdown below. The
                // original markup is kept verbatim as `legacyReport` and
                // rendered only for a candidate TalentOS has not scored yet,
                // so nobody ever opens this modal onto a dead end. Once every
                // candidate is scored, this whole binding can be deleted.
                const legacyReport = (
                  <div className="space-y-6">
                    {/* Candidate Header */}
                    <div className="border-b border-gray-200 pb-4">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-xl font-semibold text-gray-900">{result.candidate_name}</h4>
                        {result.matched_by_name && (
                          <Tooltip content={`Applied by: ${result.matched_by_name}`} position="bottom" theme="light">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 cursor-pointer transition-colors">
                              <InformationCircleIcon className="w-4 h-4 text-gray-700 hover:text-blue-600" />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{result.professional_title}</p>
                      <p className="text-sm text-gray-500">{result.candidate_email}</p>
                    </div>

                    {/* Score Breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-green-600">
                          {Math.round(result.ranking_factors?.technical_expertise ?? 0)}%
                        </div>
                        <div className="text-xs text-gray-500">Skills</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {(result.matched_skills?.length || result.detailed_analysis?.skills_breakdown?.skills_matched_count || 0)}/{((result.matched_skills?.length || 0) + (result.missing_skills?.length || 0) || result.detailed_analysis?.skills_breakdown?.total_required_skills || 0)} matched
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-orange-600">
                          {Math.round(result.ranking_factors?.leadership_potential ?? 0)}%
                        </div>
                        <div className="text-xs text-gray-500">Experience</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {result.total_years_experience || 0} years
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-purple-600">
                          {Math.round((result.confidence_level || 0) * 100)}%
                        </div>
                        <div className="text-xs text-gray-500">Education</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {result.detailed_analysis?.education_breakdown?.degree_match ? 'Degree match' : (getHighestDegreeAndField(result.detailed_analysis?.education_summary) || 'Not evaluated')}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-blue-600">
                          {Math.round((result.match_score || 0) * 100)}%
                        </div>
                        <div className="text-xs text-gray-500">Overall</div>
                        <div className="text-xs text-gray-400 mt-1">
                          Weighted average
                        </div>
                      </div>
                    </div>

                    {/* Skills Matching Details */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                        <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                        Skills Matching Details
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h6 className="text-sm font-medium text-green-700 mb-2">
                            Matched Skills ({result.matched_skills?.length || result.detailed_analysis?.skills_breakdown?.matched_skills?.length || 0})
                          </h6>
                          <div className="space-y-1 max-h-60 overflow-y-auto">
                            {(result.matched_skills || result.detailed_analysis?.skills_breakdown?.matched_skills || [])?.map((skill: any, index: number) => (
                              <div key={index} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">{skill.skill_name || skill.skill}</span>
                                <span className="text-green-600 font-medium">
                                  {Math.round((skill.match_score || 0) * 100)}%
                                </span>
                              </div>
                            ))}
                            {(!result.matched_skills && !result.detailed_analysis?.skills_breakdown?.matched_skills?.length) && (
                              <span className="text-gray-500 text-sm">No matched skills found</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <h6 className="text-sm font-medium text-red-700 mb-2">
                            Missing Skills ({result.missing_skills?.length || result.detailed_analysis?.skills_breakdown?.missing_skills?.length || 0})
                          </h6>
                          <div className="space-y-1 max-h-60 overflow-y-auto">
                            {(result.missing_skills || result.detailed_analysis?.skills_breakdown?.missing_skills || [])?.map((skill: any, index: number) => (
                              <div key={index} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">{skill.skill_name || skill.skill}</span>
                                <span className="text-red-600 font-medium">
                                  {Math.round((skill.match_score || 0) * 100)}%
                                </span>
                              </div>
                            ))}
                            {(!result.missing_skills && !result.detailed_analysis?.skills_breakdown?.missing_skills?.length) && (
                              <span className="text-gray-500 text-sm">No missing skills identified</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-gray-600">
                        <strong>Summary:</strong> {(result.matched_skills?.length || result.detailed_analysis?.skills_breakdown?.skills_matched_count || 0)} out of {((result.matched_skills?.length || 0) + (result.missing_skills?.length || 0) || result.detailed_analysis?.skills_breakdown?.total_required_skills || 0)} required skills matched
                      </div>
                    </div>

                    {/* AI Insights */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-green-50 rounded-lg p-4">
                        <h6 className="text-sm font-medium text-green-700 mb-2">Strengths</h6>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {result.ai_insights?.strengths?.map((strength: string, index: number) => (
                            <li key={index} className="flex items-start space-x-2">
                              <span className="text-green-600 mt-1">•</span>
                              <span>{strength}</span>
                            </li>
                          )) || <span className="text-gray-500">No strengths identified</span>}
                        </ul>
                      </div>
                      <div className="bg-red-50 rounded-lg p-4">
                        <h6 className="text-sm font-medium text-red-700 mb-2">Concerns</h6>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {result.ai_insights?.concerns?.map((concern: string, index: number) => (
                            <li key={index} className="flex items-start space-x-2">
                              <span className="text-red-600 mt-1">•</span>
                              <span>{concern}</span>
                            </li>
                          )) || <span className="text-gray-500">No concerns identified</span>}
                        </ul>
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h6 className="text-sm font-medium text-blue-700 mb-2">AI Recommendation</h6>
                      <p className="text-sm text-gray-700">{result.ai_insights?.recommendation || 'No recommendation available'}</p>
                    </div>
                  </div>
                )

                return (
                  <TalentosMatchReportBody
                    jobId={jobId!}
                    // resume_id, NOT candidate_id: the modal is keyed by
                    // candidate_id (resumes.candidate_id) but TalentOS records
                    // runs against the résumé that was scored.
                    resumeId={result.resume_id}
                    fallbackCandidateName={result.candidate_name}
                    renderNotScored={() => legacyReport}
                  />
                )
              })()}
            </div>

            {/* Fixed footer -- a flex-column sibling of the scrollable body
                above, structurally outside the scrolling region for the same
                reason the header is (see ActionBar's own docstring in
                TalentosMatchReport.tsx: `position: sticky` inside the
                scrollable body glitched at certain scroll positions). */}
            {(() => {
              const detailResult = comparisonResults?.comparison_results?.find(
                (r: any) => r.candidate_id === selectedCandidateForDetails
              )
              return (
                <TalentosMatchReportFooter
                  jobId={jobId!}
                  resumeId={detailResult?.resume_id ?? ''}
                  fallbackCandidateName={detailResult?.candidate_name}
                  onClose={() => setSelectedCandidateForDetails(null)}
                  onStatusChange={(newStatus, comment) =>
                    handleApplicationStatusChange(detailResult?.resume_id ?? '', newStatus, comment)
                  }
                  currentStatusId={
                    (detailResult && (applicationStatuses[detailResult.resume_id] || detailResult.candidate_status_id)) ||
                    null
                  }
                />
              )
            })()}
          </div>
        </div>
      )}

      {/* Interview Tab — hidden per request (tab entry commented out above,
          content commented out here so direct ?tab=interview navigation
          doesn't render it either)*/}
      {activeTab === 'interview' && (
        <InterviewTab
          entityType="job"
          entityId={jobId!}
          jobTitle={job?.title}
          isAdmin={canDeleteInterview}
          canManageContent={canManageInterviewContent}
          canCreateInterview={canCreateInterview}
          isActive={activeTab === 'interview'}
        />
      )}
      

      {/* {activeTab === 'voiceScreening' && (
        <VoiceScreeningTab
          jobId={jobId!}
          isActive={activeTab === 'voiceScreening'}
        />
      )} */}

      {/* can(...) added 2026-10-08 alongside the View-exclusive tab filter
          above — activeTab is restored from the URL's ?tab= param and from
          localStorage (see the "Resolve tab on mount" effect), neither of
          which checks the tabs list this content is otherwise paired with.
          Without this, a manage-only-no-view role could still land on
          ?tab=proctoredInterview directly even with the nav tab itself
          hidden. */}
      {activeTab === 'proctoredInterview' && can('proctoredInterviews.view') && (
        <ProctoredInterviewTab
          jobId={jobId!}
          jobTitle={job?.title}
          isActive={activeTab === 'proctoredInterview'}
        />
      )}

      {activeTab === 'liveInterview' && can('liveInterview.view') && (
        <LiveInterviewTab
          jobId={jobId!}
          isActive={activeTab === 'liveInterview'}
        />
      )}

      {/* Interview Modal */}
        <InterviewModal
          isOpen={showInterviewModal}
          onClose={handleCloseInterviewModal}
          guide={interviewGuide}
          loading={interviewLoading}
          onReprocess={handleReprocessInterview}
          reprocessing={interviewReprocessing}
        />

      {/* Resume Popup Modal */}
      <ResumePopupModal
        isOpen={showResumePopup}
        onClose={() => {
          setShowResumePopup(false)
          setSelectedResumeId(null)
        }}
        resumeData={resumeData}
        isLoading={resumeLoading}
        error={resumeError}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Job Posting"
      >
        Are you sure you want to delete this job? This action cannot be undone.
      </ConfirmationModal>

      {/* Edit Job Modal */}
      {(showCreateModal || editingJob) && (
        <JobForm
          job={editingJob}
          onSubmit={(data) => {
            if (editingJob && jobId) {
              // Determine if reprocessing is needed based on changed fields
              // Only reprocess if content fields changed: description, responsibilities, requirements, benefits
              const needsReprocessing = (
                data.description !== editingJob.description ||
                data.responsibilities !== editingJob.responsibilities ||
                data.requirements !== editingJob.requirements ||
                data.benefits !== editingJob.benefits
              )
              
              updateJobMutation.mutate({ id: jobId, data, needsReprocessing })
            }
          }}
          onCancel={() => {
            setShowCreateModal(false)
            setEditingJob(null)
          }}
          isLoading={updateJobMutation.isPending}
        />
      )}

      {/* Assignment Edit Popover */}
      {selectedAssignment && (
        <AssignmentEditPopover
          isOpen={showAssignmentEdit}
          onClose={() => {
            setShowAssignmentEdit(false)
            setSelectedAssignment(null)
          }}
          entityType={selectedAssignment.entityType}
          entityId={selectedAssignment.entityId}
          currentUserId={selectedAssignment.currentUserId}
          currentUserName={selectedAssignment.currentUserName}
          onSave={handleAssignmentSaved}
        />
      )}

      {/* Audit Trail Modal */}
      {selectedAssignment && (
        <AuditTrailModal
          isOpen={showAuditTrail}
          onClose={() => {
            setShowAuditTrail(false)
            setSelectedAssignment(null)
          }}
          entityType={selectedAssignment.entityType}
          entityId={selectedAssignment.entityId}
          entityTitle={selectedAssignment.entityTitle}
        />
      )}

      {/* Assignment Manager Modal */}
      {jobId && job && (
        <AssignmentManagerModal
          isOpen={showAssignmentModal}
          onClose={() => setShowAssignmentModal(false)}
          jobId={jobId}
          jobTitle={job.title}
          currentAssignments={job.assigned_recruiters || []}
          onSave={async (userIds) => {
            await updateAssignmentsMutation.mutateAsync(userIds)
          }}
          isLoading={updateAssignmentsMutation.isPending}
        />
      )}

      {/* Duplicate Detection Modal */}
      <DuplicateDetectionModal
        isOpen={showDuplicateModal}
        onClose={handleCancelUpload}
        onUploadAnyway={handleUploadAnyway}
        duplicateData={duplicateData}
        isUploading={uploadingResume}
      />

      {/* Bulk apply + duplicate queue are rendered app-wide by BulkApplyProvider
          so they survive navigation — see contexts/BulkApplyContext.tsx */}
    </div>
  )
}

export default JobDetailPage